/// <reference path="../../../typescript_definitions/declarations/All.d.ts" />

console.log('[MODULE BUILD MARKER] v=debouncedRefresh-50ms-diagnostic-2');

import { AndroidAR, FocusAttributesEventListener, SceneViewer, SceneViewerOptions } from '@zakeke/zakeke-3d-core';
import _, { debounce, sortBy } from 'lodash';
import {
	CompositionClientPreview,
	DefaultDeepARFineTuningSettings,
	EventMessage,
	ExtensionFields,
	GraphQLWelcomeMessageResponse,
	ItemType,
	OnBeforeSendDataToParent,
	PreviewCameraImage,
	SceneAnimationsConfig,
	SellerTryOnState,
	TemplateComposition,
	ThemeCategory,
	ThemeMacroCategory,
	ThemeTemplateGroup,
	TryOnMode,
	TryOnProvidersTypesMapping,
	VisibleEventMessage
} from '.';
import { DesignPriceCalculatorRuleBased, DesignPriceCalculatorRuleFactory } from '../../../libs/pricingRuleBase/DesignPriceCalculatorRuleBased';
import { axiosApi, setupApi } from './api';
import { Exception, ParametersParseFailException } from './exceptions';
import { isFeatureEnabled } from './generated/feature-flags';
import { downloadFontsOfDesign, loadFonts, sanitizeString, sanitizeText } from './fontsManager';
import {
	IS_ANDROID,
	IS_AR_DEVICE_COMPLIANT,
	IS_IOS,
	ascComparator,
	compare,
	iOS,
	isPlatformCode,
	matchCodes,
	rgb2hex,
	sleep,
	sanitizeTextFromUnsupportedAndEmojiCharacters
} from './helpers';
import ImageUploadTask from './imageUploadTask';
import { Integration, PlatformAttributeSelection } from './integrations/Integration';
import { integrationFor } from './integrations/helpers';
import {
	Attribute,
	AttributeOptionShapeType,
	AttributeType,
	BootParameters,
	Camera,
	Composition,
	ExtensionFieldDataType,
	FontFamily,
	Group,
	ImageItem,
	ImageMacroCategory,
	InternalTranslations,
	Item,
	Option,
	Product,
	ProductQuantityRule,
	Quote,
	SelectedOption,
	SellerSettings,
	Settings,
	Step,
	Template,
	TemplateMacroCategory,
	TextItem,
	Translations,
	TryOnGenericFineTuningSettings,
	TryOnProduct,
	TryOnSeller,
	ZakekeEvent,
	AIPresetCategory,
	AIPreset,
	AIProductStagingRule,
	AIProductStagingOptions,
	AIProductStagingResult,
	AIVirtualTryOnOptions,
	AIVirtualTryOnResult,
	AIServicesAvailability,
	AIVirtualModelRule,
	AIToolUsageRule,
	AILimitPeriodType,
	AIRateLimitError
} from './models';

import axios, { AxiosResponse } from 'axios';
import {
	DAMViewerAnalyticsEventType,
	DAMViewerAnalyticsType,
	PreviewFile,
	PrintingMethodsRestrictions,
	TextSanitationResult,
	UIConfig,
	ZakekeImage
} from './models';
import { request } from 'http';

declare global {
	interface Window {
		token: string;
		isOwin: boolean;
	}
}

interface RestrictionColor {
	colorCode: string;
	isDefault: boolean;
}

interface CustomPreviewSize {
	customCartPreviewWidth: number;
	customCartPreviewHeight: number;
}

interface ThemeCompositions {
	name: string;
	docID: string;
	previewImageUrl: string;
	tags: string[];
}

interface CurrentCompositionInfo {
	compositionId: string | null;
	compositionName: string | null;
	compositionTags: string[] | null;
}

interface StaticTranslation {
	original: string;
	localization: string;
	lcid: number;
}

interface DynamicTranslation {
	original: string;
	localization: string;
	lcid: number;
}

interface TemplateCategory {
	categoryName: string;
	categoryID: number;
}

interface Category {
	categoryID: number;
	macroCategoryID: number;
	name: string;
	description: string;
	sellerID: number;
	displayOrder: number;
	isDraft: boolean;
	composition: any;
	template: any;
}

interface MacroCategory {
	macroCategoryID: number;
	name: string;
	sellerID: number;
	displayOrder: number;
	isDraft: boolean;
	category: Category[];
}

interface AITaskResponse {
	state: number;
	resultUrl?: string;
	errorMessage?: string;
}

export class ZakekeEnvironment {
	private booted = false;

	private token!: string;
	private platformAttributesSelection: PlatformAttributeSelection[] = [];
	private settings!: Settings;
	private platform!: string;
	private viewer!: SceneViewer;
	private selection!: Zakeke.ComposerSelection;
	private integration!: Integration;
	private previewWidth = 1024;
	private previewHeight = 1024;
	private clientPreviewWidth = 1024;
	private clientPreviewHeight = 1024;
	private shareUrlPrefix: string | null = null;
	private events: ZakekeEvent[] = [];
	private zkProduct: MPlaza.Model | null = null;
	private zkScene: Zakeke.Scene | null = null;
	private zkCurrentTemplate: MPlaza.Design | null | undefined = null;
	private zkTemplates: MPlaza.Design[] = [];
	private internalIsFullscreen: boolean = false;
	private highlightSettings: { color: string; size: number } | null = null;
	private previousMeshesVisibleStates: { [key: string]: boolean } = {};
	private lastCameraLocation: string | null | undefined = null;
	private enableShareCompositionUrl: boolean = false;
	private sharedCompositionDocId: string | null = null;
	private templateId: string | null = null;
	public deepARsceneGLBUrl: string | null = '';
	public loadedComposition: {
		templateName: string;
		attributesOptions: Map<number, number>;
		selectedCategoryID: number | null;
	} | null = null;
	private savedCompositions: Composition[] | null = null;
	private isDraftEditor: boolean = false;
	private isViewerMode: boolean = false;
	private isDAMViewer: boolean = false; // if DAM viewer
	private isPresetConfigurationEditor: boolean = false;
	public shouldLoadDesign: boolean | null = null;
	private debugMode = false;
	private draftCompositionDocId: string | null = null;
	private initialSelection!: Zakeke.ComposerSelection;
	private undoStack: any[] = [];
	private redoStack: any[] = [];
	private pricingCalculator!: DesignPriceCalculatorRuleBased;

	public currentAttributesSelection: Object | null = null;
	public currentCompositionInfo: CurrentCompositionInfo | null = null;
	private copyrightMessageLocalStorageKey: string = '';
	private isPupillaryDistanceMandatory: boolean = false;
	public isAIEnabled: boolean = false;
	public isAIStudioShoppingPreviewEnabled: boolean = false;
	public isAIStudioEditorEnabled: boolean = false;
	public isAIStudioEnabled: boolean = false;
	public isAiProductStagingEnabled: boolean = false;
	public isAiVtoEnabled: boolean = false;
	private lastProductStagingGenerationTime: number | undefined = undefined;
	private lastVirtualTryonGenerationTime: number | undefined = undefined;
	/** Session ID (GUID) persisted for the lifetime of this environment instance, sent as ZKK-Session-ID header */
	private readonly aiSessionId: string = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		const v = c === 'x' ? r : (r & 0x3) | 0x8;
		return v.toString(16);
	});
	public sellerSettings: SellerSettings | null = null;
	public get internalProduct() {
		return this.zkProduct;
	}

	public get internalScene() {
		return this.zkScene;
	}

	public get internalCurrentTemplate() {
		return this.zkCurrentTemplate;
	}

	public themeCompositions: ThemeCompositions[] | null = null;
	public currency: string = 'EUR';
	public groups: Group[] = [];
	public fonts: FontFamily[] = [];
	public defaultFontID: number | null = null;
	public disableTextColors: boolean = false;
	public textColors: RestrictionColor[] = [];

	/**
	 * Get the current text default color.
	 */
	public defaultColor: string = '';

	public quantity: number = 1;
	public quantityRule: ProductQuantityRule | null = null;
	public culture: string = 'en-US';
	public productCode!: string;
	public productName!: string;
	public price: number = 0;
	public isOutOfStock: boolean = false;
	public composition!: any;
	public compositionId: string | null = null;
	public templates: Template[] = [];
	public currentTemplate: Template | null = null;
	public product: Product | null = null;
	public templateMacroCategories: TemplateMacroCategory[] | null = null;
	public selectedTemplateCategory: string | null = null;
	public cameras: Camera[] = [];
	public isExplodedMode: boolean = false;
	public isAssetsLoading = false;
	public additionalCustomProperties: ExtensionFields[] = [];
	public eventMessages: EventMessage[] | null = null;
	public personalizedMessages: EventMessage[] | null = null;
	public visibleEventMessages: VisibleEventMessage[] = [];
	public tryOnProvidersSettings: TryOnProvidersTypesMapping | null = null;
	public tryOnProductSettings: TryOnProduct | null = null;
	public tryOnSellerSettings: TryOnSeller | null = null;
	public pdSettings: number = -1;
	public isInfoPointContentVisible: boolean = false;
	public isCompositionSaving: boolean = false;
	public imagesMacroCategories: ImageMacroCategory[] = [];

	private internalTranslations: InternalTranslations = {
		statics: [],
		dynamics: []
	};
	public areTranslationsLoaded: boolean = false;

	private integrationVersion: number = 1;
	public useLegacyScreenshot: boolean = false;

	public visibleCustomizer: MPlaza.Customizer | null = null;

	public get isFullscreenMode() {
		let doc = document as any;
		return Boolean(doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement || doc.msFullscreenElement);
	}

	public get userSettings() {
		return this.settings;
	}

	public get isViewerReady() {
		return !!this.viewer;
	}

	public get isShoppingPreview() {
		return this.isViewerMode || this.platform.toLowerCase() == 'preview';
	}

	public uiConfig: UIConfig | null = null;
	public animationsConfig: SceneAnimationsConfig | null = null;

	public backgroundColor: string | null = null;
	public removeBackground: boolean = false;

	constructor() {
		if (IS_IOS) {
			this.previewHeight = 512;
			this.previewWidth = 512;
		}
	}

	private fetchVisitorToken = async (userId) => {
		try {
			const response = await fetch(`${(window as any).Zakeke.config.baseApiUrl}user/visitor_token?userID=${userId}`);
			const token = await response.text();
			console.log("Sono il token recuperato a partire dall'userID", token);
			this.token = token;
			return token;
		} catch (error) {
			console.error('Errore nel recupero del token:', error);
			return null;
		}
	};

	private parseParameters = (parameters?: any) => {
		console.info('Parameters', parameters);

		const parseSecure = (value: string) => {
			//return decodeURIComponent(fixedEncodeURIComponent(value.toString().replace(/\+/g, ' ')));
			return decodeURIComponent(value.toString().replaceAll('%', '').replace(/\+/g, '%20'));
		};

		const parseSingleParameter = (key: string, value: string | null) => {
			if (!value) return;

			switch (key) {
				case 'name':
					this.productName = parseSecure(value);
					break;
				case 'ecommerce':
					this.platform = decodeURIComponent(value);
					break;
				case 'qty':
					this.quantity = parseFloat(decodeURIComponent(value));
					break;
				case 'culture':
					this.culture = decodeURIComponent(value);
					break;
				case 'currency':
					this.currency = decodeURIComponent(value);
					break;
				case 'token':
					let tokenFromParams = decodeURIComponent(value);
					if (tokenFromParams) this.token = tokenFromParams;
					break;
				case 'compositionId':
					this.compositionId = decodeURIComponent(value);
					break;
				case 'modelCode':
					this.productCode = parseSecure(value);
					break;
				case 'previewWidth':
					this.previewWidth = parseInt(parseSecure(value));
					break;
				case 'previewHeight':
					this.previewHeight = parseInt(parseSecure(value));
					break;
				case 'shareUrlPrefix':
					this.shareUrlPrefix = parseSecure(value);
					break;
				case 'sharedCompositionDocId':
					this.sharedCompositionDocId = parseSecure(value);
					break;
				case 'templateId':
					this.templateId = parseSecure(value);
					break;
				case 'isDraftEditor':
					this.isDraftEditor = true;
					break;
				case 'enableShareCompositionUrl':
					this.enableShareCompositionUrl = true;
					break;
				case 'draftCompositionDocId':
					this.draftCompositionDocId = value;
					break;
				case 'viewerMode':
					this.isViewerMode = true;
					break;
				case 'isTemplateEditor':
					this.isPresetConfigurationEditor = true;
					break;
				case 'isAIStudioEditor':
					this.isAIStudioEditorEnabled = true;
					break;
				case 'selectedTemplateCategory':
					this.selectedTemplateCategory = parseSecure(value);
					break;
				case 'legacyScreenshot':
					this.useLegacyScreenshot = value == '1';
					break;
				case 'integrationVersion':
					this.integrationVersion = parseInt(value) ?? 1;
					break;
				case 'dam':
					this.isDAMViewer = value == '1' || value == 'true';
					break;
				case 'debugMode':
					this.debugMode = value == '1' || value == 'true';
					break;
			}
		};

		// From parameters or URL
		if (parameters) {
			for (const key in parameters) {
				const value = parameters[key];

				parseSingleParameter(key, value);

				// Attributes collection
				if (key.indexOf('attributes') == 0) {
					for (let attr of value) {
						// In some special cases, the plugin could pass empty options (e.g. seat and tools)
						// In this cases, ignore them and take the Zakeke one.
						if (attr[0].id === '' || attr[1].id === '') continue;

						// For each attribute/option
						this.platformAttributesSelection.push(
							new PlatformAttributeSelection(JSON.stringify(attr[0]), JSON.stringify(attr[1]), '', '', true, true)
						);
					}
				}
			}
		} else {
			let queryString = window.location.href.substring(window.location.href.lastIndexOf('?') + 1);
			let pieces = queryString.split('&');

			for (let i = 0; i < pieces.length; i++) {
				let index = pieces[i].indexOf('=');
				if (index == -1) {
					throw new ParametersParseFailException(pieces[i]);
				}

				let key = decodeURIComponent(pieces[i].substring(0, index));
				let value = pieces[i].substring(index + 1);

				parseSingleParameter(key, value);

				// Attributes collection
				if (key.indexOf('attributes') == 0) {
					let data = decodeURIComponent(value.replace(/\+/g, '%20'));
					if (data && data != '') {
						let json = JSON.parse(data);

						for (let attr of json) {
							// In some special cases, the plugin could pass empty options (e.g. seat and tools)
							// In this cases, ignore them and take the Zakeke one.
							if (attr[0].id === '' || attr[1].id === '') continue;

							this.platformAttributesSelection.push(
								new PlatformAttributeSelection(JSON.stringify(attr[0]), JSON.stringify(attr[1]), '', '', true, true)
							);
						}
					}
				}
			}
		}

		// Parameter validation
		const requiredParams = {
			'culture (in xx_XX format)': this.culture,
			currency: this.currency,
			'name (product name)': this.productName,
			ecommerce: this.platform,
			'qty (product quantity)': this.quantity,
			modelCode: this.productCode
		};

		for (const key in requiredParams) {
			if (!requiredParams[key as keyof typeof requiredParams]) throw new Exception('Missing parameter: ' + key);
		}

		window['token'] = this.token;
		window['isOwin'] = true;
		return true;
	};

	public async makeDAMAnalyticsView(type: DAMViewerAnalyticsEventType) {
		const body: DAMViewerAnalyticsType = {
			modelCode: this.productCode || '',
			userId: this.settings?.userID || 0,
			eventType: type
		};
		return axios.post(Zakeke.config.damSettings.analyticsUrl, body);
	}

	// Get product
	private getProduct = async () => {
		if (!this.productCode || this.productCode == '') throw new Exception('Loading product but no SKU found');

		return new Promise((resolve, reject) => {
			const zkModel = new MPlaza.Model();
			zkModel.set('modelID', 1);
			zkModel.fetch({
				success: () => {
					resolve(zkModel);
				},
				error: () => {
					reject(new Exception('Failed loading product'));
				},
				data: {
					c: this.productCode
				}
			});
		});
	};

	// Get scene
	private getScene = async (product: any) => {
		if (!product) throw new Exception('Loading scene but no product found');

		if (product.get('scenes').length == 0) throw new Exception("The product don't have any scene configured");

		const zkScene = new Zakeke.Scene();
		zkScene.set('sceneID', product.get('scenes').at(0).get('sceneID')!);

		return new Promise((resolve, reject) => {
			zkScene.fetch({
				success: (zkScene: any) => {
					resolve(zkScene);
				},
				error: () => {
					reject(new Exception('Failed loading scene'));
				}
			});
		});
	};

	// Get composer models
	private getComposerModels = async (): Promise<any> => {
		if (!this.productCode || this.productCode == '') throw new Exception('Loading product but no SKU found');
		return new Promise((resolve, reject) => {
			const models = new Zakeke.ComposerModels();
			models.set('modelID', 1);
			models.fetch({
				success: () => {
					resolve(models);
				},
				error: () => {
					reject(new Exception('Failed loading product'));
				},
				data: {
					c: this.productCode
				}
			});
		});
	};

	// Get fonts
	private getFonts = async () => {
		let fonts = (await axiosApi.get<FontFamily[]>('fontfamilies/')).data;
		let productFonts =
			this.zkProduct
				?.get('printTypes')
				.at(0)
				.get('fonts')
				.map((x) => x.get('fontFamilyID')) ?? [];

		if (productFonts.length > 0) fonts = fonts.filter((font) => productFonts.indexOf(font.fontFamilyID) != -1);

		return fonts;
	};

	// Public method to get font weights - delegates to viewer
	public getFontWeights = (font: FontFamily) => {
		if (!this.viewer || !font) {
			console.warn('[Environment.getFontWeights] No viewer or font is null, returning defaults');
			return {
				normal: false,
				bold: false,
				italic: false,
				italicBold: false,
				normalWeight: 400,
				boldWeight: 700,
				italicWeight: 400,
				italicBoldWeight: 700
			};
		}

		const weightsArray = this.viewer.getFontWeights(font);

		// Convert from array format to provider format (booleans + numbers)
		const normalWeight = weightsArray.find((w) => w.style === 'normal' && w.weight <= 500);
		const boldWeight = weightsArray.find((w) => w.style === 'normal' && w.weight > 500);
		const italicWeight = weightsArray.find((w) => w.style === 'italic' && w.weight <= 500);
		const italicBoldWeight = weightsArray.find((w) => w.style === 'italic' && w.weight > 500);

		const result = {
			normal: normalWeight !== undefined,
			bold: boldWeight !== undefined,
			italic: italicWeight !== undefined,
			italicBold: italicBoldWeight !== undefined,
			normalWeight: normalWeight?.weight || 400,
			boldWeight: boldWeight?.weight || 700,
			italicWeight: italicWeight?.weight || 400,
			italicBoldWeight: italicBoldWeight?.weight || 700
		};

		return result;
	};

	// Update font weight based on current bold/italic state and font capabilities
	private updateFontWeightForTextItem = (guid: string, font: FontFamily, requestedBold?: boolean, requestedItalic?: boolean) => {
		if (!this.viewer || !font) return;

		// Get current item to read current styles if not provided
		const item = this.getDesignItemsInfo().find((di) => di.guid === guid) as TextItem;
		if (!item) {
			console.warn('[updateFontWeightForTextItem] Item not found:', guid);
			return;
		}

		// Determine current styles (use provided values or parse from item)
		let currentIsBold: boolean;
		let currentIsItalic: boolean;

		if (requestedBold !== undefined || requestedItalic !== undefined) {
			// Called from setItemBold/Italic - use provided values
			const itemWeight = item.fontWeight.split(' ');
			currentIsBold = requestedBold ?? (itemWeight.length > 1 ? parseInt(itemWeight[1]) > 500 : parseInt(itemWeight[0]) > 500);
			currentIsItalic = requestedItalic ?? (itemWeight.length > 1 && itemWeight[0] === 'italic');
		} else {
			// Called from setItemFontFamily - read from current item
			const itemWeight = item.fontWeight.split(' ');
			currentIsBold = itemWeight.length > 1 ? parseInt(itemWeight[1]) > 500 : parseInt(itemWeight[0]) > 500;
			currentIsItalic = itemWeight.length > 1 && itemWeight[0] === 'italic';
		}

		// Check what styles are available in the font
		const fontWeightsArray = this.viewer.getFontWeights(font);
		const hasBold = fontWeightsArray.some((w) => w.style === 'normal' && w.weight > 500);
		const hasItalic = fontWeightsArray.some((w) => w.style === 'italic');

		// Handle BOLD: apply if requested AND available, otherwise deactivate
		if (requestedBold !== undefined) {
			// Explicit request from setItemBold
			if (requestedBold && hasBold) {
				this.viewer.setDesignItemBold(guid, true, font, this.visibleCustomizer);
			} else {
				this.viewer.setDesignItemBold(guid, false, font, this.visibleCustomizer);
			}
		} else if (currentIsBold) {
			// Font change: re-apply only if available
			if (hasBold) {
				this.viewer.setDesignItemBold(guid, true, font, this.visibleCustomizer);
			} else {
				this.viewer.setDesignItemBold(guid, false, font, this.visibleCustomizer);
			}
		}

		// Handle ITALIC: apply if requested AND available, otherwise deactivate
		if (requestedItalic !== undefined) {
			// Explicit request from setItemItalic
			if (requestedItalic && hasItalic) {
				this.viewer.setDesignItemItalic(guid, true, font, this.visibleCustomizer);
			} else {
				this.viewer.setDesignItemItalic(guid, false, font, this.visibleCustomizer);
			}
		} else if (currentIsItalic) {
			// Font change: re-apply only if available
			if (hasItalic) {
				this.viewer.setDesignItemItalic(guid, true, font, this.visibleCustomizer);
			} else {
				this.viewer.setDesignItemItalic(guid, false, font, this.visibleCustomizer);
			}
		}
	};

	// Check if font supports specific styles
	public checkFontSupports = (fontName: string, needsBold: boolean, needsItalic: boolean) => {
		if (!this.viewer) return { canKeepStyles: false, supportsBold: false, supportsItalic: false };

		const font = this.fonts.find((f) => f.name === fontName);
		if (!font) return { canKeepStyles: false, supportsBold: false, supportsItalic: false };

		// SceneViewer.getFontWeights returns an ARRAY
		const fontWeightsArray = this.viewer.getFontWeights(font);

		const supportsBold = fontWeightsArray.some((w) => w.style === 'normal' && w.weight > 500);
		const supportsItalic = fontWeightsArray.some((w) => w.style === 'italic');

		const canKeepStyles = (!needsBold || supportsBold) && (!needsItalic || supportsItalic);

		return { canKeepStyles, supportsBold, supportsItalic };
	};

	// Get settings
	private getSettings = async () => {
		const settings = (await axiosApi.get<Settings>('customizerinfo/')).data;
		return settings;
	};

	private getSellerSetting = async () => {
		return new Promise<SellerSettings | null>(async (resolve, reject) => {
			// Fetch the seller setting
			const sellerSetting: SellerSettings | null = (await axiosApi.get('sellerSetting/')).data;
			resolve(sellerSetting);
		});
	};

	// Keep quote FormData consistent with the seller's full field schema (typeField/label/required/...).
	// Older / 3rd-party submit paths can post a bare [{ name, value }] array that drops the field
	// metadata. When that happens, rebuild each submitted entry from the product's quote-rule schema
	// (matched by field name), preserving only the user-entered value, so the stored FormData stays
	// in the rich shape. Safe no-op when the data is already rich or the schema is unavailable.
	private normalizeQuoteFormData = (formData: any) => {
		if (!Array.isArray(formData) || formData.length === 0) return formData;

		// Already rich: every entry carries its field definition.
		if (!formData.some((item) => item && item.typeField === undefined)) return formData;

		const schemaRaw = this.product?.quoteRule?.formControlsData;
		if (!schemaRaw) return formData;

		let schema: any[];
		try {
			schema = JSON.parse(schemaRaw);
		} catch {
			return formData;
		}
		if (!Array.isArray(schema)) return formData;

		// Index the rich field definitions by name.
		const schemaByName = new Map<string, any>();
		schema.forEach((field) => {
			if (field && field.name !== undefined && field.typeField !== undefined) schemaByName.set(field.name, field);
		});
		if (schemaByName.size === 0) return formData;

		// Upgrade bare entries to their full definition, keeping the submitted value. Never drops data.
		return formData.map((item) => {
			if (!item || item.typeField !== undefined) return item;
			const field = schemaByName.get(item.name);
			return field ? { ...field, value: item.value } : item;
		});
	};

	// Create quote for composition
	public createQuote = async (formData: any) => {
		let quote = new Quote();
		quote.formData = JSON.stringify(this.normalizeQuoteFormData(formData));
		quote.quantity = this.quantity;
		quote.compositionDocID = this.composition.get('docID');
		quote.modelPrice = (await this.getPrice()).price;
		quote.variantName = this.product!.name ?? '';

		const quoteCreated = (await axiosApi.post(Zakeke.config.baseApiUrl + 'quotes', quote)).data;
		return quoteCreated;
	};

	private getPrice = async () => {
		let price = 0;
		let isOutOfStock = false;

		console.log('Get price from store (integration version ' + this.integrationVersion + ')...');

		if (!(this.zkScene && this.zkScene.get('url') && this.zkScene!.get('url')!.endsWith('.glb'))) {
			const result = await this.integration.getPrice(
				this.composition,
				this.zkCurrentTemplate,
				this.zkProduct,
				this.platformAttributesSelection,
				this.composition.getPrice(this.zkProduct, this.quantity),
				this.settings.additionalFeatures?.prices3dConfigurator ?? false,
				this.settings.isPricingAdvancedEnabled,
				this.quantity
			);

			if (this.integrationVersion === 1) {
				price = result as number;
			} else if (this.integrationVersion === 2) {
				const objectResult = result as { price: number; isOutOfStock: boolean };
				price = objectResult.price;
				isOutOfStock = objectResult.isOutOfStock;
			}
		}

		console.log('Price from store: ' + price);
		return { price, isOutOfStock };
	};

	private getTranslations = async () => {
		try {
			const response = await axiosApi.get('sellerLocalizations/customizer-localizations');

			if (response) {
				return response.data;
			} else {
				console.log('getTranslations response is empty');
			}
		} catch (e) {
			console.error("'getTranslations' error", e);
		}
	};

	private loadLocalization = async () => {
		try {
			let lang = this.culture;

			// Try zh-TW
			if (Zakeke.config.avaibleLanguages.indexOf(lang) == -1) lang = this.culture.substring(0, 2);

			// Try zh
			if (Zakeke.config.avaibleLanguages.lastIndexOf(lang) == -1) lang = 'en';

			await this.loadScript(Zakeke.config.localizationsUrl + `${lang.toLowerCase()}/composer.js`);
		} catch (e) {
			console.error('Failed loading localization', e);
		}
	};

	public getTemplateMacroCategories = async () => {
		const response = await axiosApi.get<MacroCategory[]>('compositionmacrocategories');
		let categories: TemplateMacroCategory[] | null = response.data.map((macroCategory) => {
			let allCategories: TemplateCategory[] = macroCategory.category.map((category) => {
				return { categoryName: category.name, categoryID: category.categoryID };
			});
			return {
				macroCategoryName: macroCategory.name,
				macroCategoryID: macroCategory.macroCategoryID,
				categories: allCategories
			};
		});
		return categories;
	};

	private loadScript = async (src: string) => {
		return new Promise<void>((resolve, reject) => {
			var script = document.createElement('script');
			script.onload = () => resolve();
			script.onerror = () => resolve();
			script.src = src;
			document.body.appendChild(script);
		});
	};

	public setCopyrightMessageAccepted = (copyrightMandatoryCheckbox: boolean) => {
		try {
			if (copyrightMandatoryCheckbox) window.localStorage.setItem(this.copyrightMessageLocalStorageKey, copyrightMandatoryCheckbox.toString());
			else window.localStorage.removeItem(this.copyrightMessageLocalStorageKey);
		} catch (e) {}
	};

	public getCopyrightMessageAccepted = () => {
		let copyrightMessageAccepted = false;
		try {
			copyrightMessageAccepted = window.localStorage.getItem(this.copyrightMessageLocalStorageKey) ? true : false;
		} catch (e) {}
		return copyrightMessageAccepted;
	};

	/**
	 * @private
	 */
	public boot = async (parameters: BootParameters | null) => {
		// React in debug mode could run two times the use effects
		// and run the boot twice.
		if (this.booted) return;

		this.booted = true;
		const params = new URLSearchParams(window.location.search);
		const userID = params.get('userID');
		// token per viewer
		if (userID) await this.fetchVisitorToken(userID);

		this.parseParameters(parameters);
		setupApi(this.culture, this.token);
		if (!(this.zkScene && this.zkScene.get('url') && this.zkScene!.get('url')!.endsWith('.glb'))) await this.loadLocalization();

		this.settings = await this.getSettings();
		this.copyrightMessageLocalStorageKey = this.settings.userID + '_copyright_accepted';
		this.eventMessages = this.settings.eventMessages.map((message) => EventMessage.fromMessage(message));
		console.log(this.eventMessages, 'eventMessages');
		this.isAIEnabled = this.settings.additionalFeatures?.aiPoweredTools ?? false;

		// Fetch granular AI service availability
		const servicesAvailability = await this.fetchServicesAvailability();
		const imageAIFeatureEnabled = isFeatureEnabled('imageAI');

		console.log('[AI-Studio] servicesAvailability:', servicesAvailability);
		console.log('[AI-Studio] imageAI featureFlag:', imageAIFeatureEnabled);
		console.log('[AI-Studio] isViewerMode:', this.isViewerMode);

		// Granular per-service flags (available in both viewer and non-viewer modes)
		this.isAiProductStagingEnabled = imageAIFeatureEnabled && servicesAvailability.GENERATIVE_AI_PRODUCT_STAGING;
		this.isAiVtoEnabled = imageAIFeatureEnabled && servicesAvailability.GENERATIVE_AI_VTO;

		console.log('[AI-Studio] isAiProductStagingEnabled:', this.isAiProductStagingEnabled);
		console.log('[AI-Studio] isAiVtoEnabled:', this.isAiVtoEnabled);

		// AI Studio Shopping Preview - Shows button that fires postMessage and saves composition
		// Only available in viewer mode with imageAI feature flag
		this.isAIStudioShoppingPreviewEnabled = imageAIFeatureEnabled && this.isViewerMode;

		// AI Studio - Master flag for non-viewer (theme configurator) mode only
		// Viewer uses isAiProductStagingEnabled/isAiVtoEnabled directly
		this.isAIStudioEnabled = !this.isViewerMode && (this.isAiProductStagingEnabled || this.isAiVtoEnabled);

		console.log('[AI-Studio] isAIStudioEnabled (theme master):', this.isAIStudioEnabled);
		console.log('[AI-Studio] isAIStudioShoppingPreviewEnabled:', this.isAIStudioShoppingPreviewEnabled);

		this.trigger('change');

		this.trigger('change');
		const composerModels = await this.getComposerModels();
		if (!composerModels.get('scene').get('config')) composerModels.set('config', composerModels.get('model').get('sceneConfig'));
		this.zkScene = composerModels.get('scene');
		this.zkProduct = composerModels.get('model');

		// Refine AI flags with product-level rules check
		const productModelId = this.zkProduct?.get('modelID');
		if (productModelId) {
			if (this.isAiProductStagingEnabled) {
				const stagingRules = await this.getApplicableAIStagingRules({ productID: productModelId });
				this.isAiProductStagingEnabled = !!stagingRules;
			}
			if (this.isAiVtoEnabled) {
				const vtoRules = await this.getApplicableAIVirtualModelRules({ productID: productModelId });
				this.isAiVtoEnabled = !!vtoRules;
			}
			// Update master flag after product-level check
			this.isAIStudioEnabled = !this.isViewerMode && (this.isAiProductStagingEnabled || this.isAiVtoEnabled);

			console.log('[AI-Studio] After product rules check - isAiProductStagingEnabled:', this.isAiProductStagingEnabled);
			console.log('[AI-Studio] After product rules check - isAiVtoEnabled:', this.isAiVtoEnabled);
			console.log('[AI-Studio] After product rules check - isAIStudioEnabled:', this.isAIStudioEnabled);
		}

		// Initialize defaultFontID from product
		this.defaultFontID = this.zkProduct?.get('printTypes')?.at(0)?.get('printTypeDefaultFontFamilyID') ?? null;

		const statusID = this.zkProduct?.get('statusID');
		console.log('sono prima del customEvent', statusID);
		if (
			(statusID === 2 && this.zkScene && this.zkScene.get('url') && this.zkScene!.get('url')!.endsWith('.glb')) ||
			(statusID === 2 && !this.isShoppingPreview)
		) {
			console.log('Product unpublished, triggering event');
			const event = new CustomEvent('productUnpublished', {
				detail: { statusID }
			});
			window.dispatchEvent(event);
		} else {
			// fill the uiconfig with zkscene config url loader and ar icon
			if (this.zkScene?.get('config')) {
				console.log('fill UIconfig and animations');
				if (this.zkScene?.get('config')?.get('uiConfig'))
					this.uiConfig = {
						urlLoader: this.zkScene.get('config')?.get('uiConfig')?.get('urlLoader') ?? '',
						urlARIcon: this.zkScene.get('config')?.get('uiConfig').get('urlARIcon') ?? ''
					};
				if (this.zkScene?.get('config')?.get('animations'))
					this.animationsConfig = {
						runAtStart: this.zkScene.get('config')?.get('animations')?.get('runAtStart') ?? false,
						loop: this.zkScene.get('config')?.get('animations')?.get('loop') ?? false,
						speed: this.zkScene.get('config')?.get('animations')?.get('speed') ?? 1
					};
			}
			this.backgroundColor = this.zkScene?.get('config')?.get('general')?.get('backgroundColor') ?? null;
			this.removeBackground = this.zkScene?.get('config')?.get('general')?.get('removeDefaultBackground') ?? false;
			if (this.zkScene?.get('config')) this.product = Product.fromProduct(this.zkProduct);
			this.quantityRule = this.zkProduct!.get('quantityRule') ? ProductQuantityRule.fromQuantityRule(this.zkProduct!.get('quantityRule')) : null;

			let promises: any = [];

			if (!(this.zkScene && this.zkScene.get('url') && this.zkScene!.get('url')!.endsWith('.glb')))
				promises = [
					this.getTranslations(),
					integrationFor(this.platform),
					this.getSellerSetting(),
					this.getTemplateMacroCategories(),
					DesignPriceCalculatorRuleFactory.create(this.zkProduct, Zakeke.config.baseApiUrl, this.token, true, Zakeke.config.cloudFlareApiUrl),
					this.getFonts(),
					this.getTemplates(),
					this.getMessagesByProductCode(this.productCode)
				];

			if (this.zkScene && this.zkScene.get('config') && this.zkScene!.getTryOnEnabled()) {
				const tryonPromises = [this.getTryOnProvidersList(), this.getTryOnProductInfo()];
				promises.push(...tryonPromises);
			}

			if (this.product?.areas && this.product.areas.length > 0) {
				promises.push(this.getMacroCategories());
			}

			if (!(this.zkScene && this.zkScene.get('url') && this.zkScene!.get('url')!.endsWith('.glb')))
				await Promise.all(promises).then(
					([
						translations,
						integration,
						sellerSettings,
						templateMacroCategories,
						pricingCalculator,
						fonts,
						zkTemplates,
						personalizedMessages,
						imageMacroCategories
					]) => {
						this.copyrightMessageLocalStorageKey = this.settings.userID + '_copyright_accepted';
						this.eventMessages = this.settings.eventMessages.map((message) => EventMessage.fromMessage(message));

						this.internalTranslations = translations;
						this.areTranslationsLoaded = translations ? true : false;
						console.log('translations', translations);
						console.log(personalizedMessages, 'personalizedMessages');
						this.personalizedMessages = personalizedMessages;

						console.log('areas di product', this.product?.areas);
						if (this.product?.areas && this.product.areas.length > 0) this.imagesMacroCategories = imageMacroCategories;

						this.integration = integration;
						this.sellerSettings = sellerSettings ? SellerSettings.fromSellerSettings(sellerSettings) : null;
						this.templateMacroCategories = templateMacroCategories;

						this.pricingCalculator = pricingCalculator;

						if (this.zkProduct!.get('colors').length > 0) {
							this.pricingCalculator.colorID = this.zkProduct!.get('colors').at(0).get('colorID');
						}

						this.integration.setPricingCalculator(this.pricingCalculator);

						this.fonts = fonts;
						const restriction = this.getPrintingMethodsRestrictions();
						this.defaultColor = restriction.defaultColor;
						this.disableTextColors = restriction.disableTextColors;
						this.textColors = restriction.textColors;

						this.zkTemplates = zkTemplates;
						this.templates = this.zkTemplates.map((zkTemplate) => Template.fromTemplate(zkTemplate));
						this.cameras = this.zkScene?.get('cameraLocations').map((x) => Camera.fromZkCamera(x)) ?? [];
					}
				);
			else await Promise.all(promises).then(() => console.log('Promises for viewer resolved'));
			if (!this.zkScene) throw new Error('Product has no 3D scene.');

			if (this.tryOnProductSettings) {
				this.getTryOnSellerInfo(this.tryOnProductSettings.typeID, this.tryOnProductSettings.providerID);
			}

			if (this.debugMode) this.trigger('debugMode');
			this.trigger('change');

			this.viewer = await SceneViewer.createSceneViewerAsync({ isViewer: this.isDAMViewer } as SceneViewerOptions);
			this.trigger('viewer', this.viewer);
			this.viewer.isHologramSource = true;

			this.viewer.addInfoPointPickedListener(() => {
				this.isInfoPointContentVisible = true;
				this.trigger('change');
			});

			this.viewer.addInfoPointContentHiddenListener(() => {
				this.isInfoPointContentVisible = false;
				this.trigger('change');
			});

			this.viewer.addBeginAssetsLoadingListener(() => {
				this.isAssetsLoading = true;
				this.trigger('change');
			});

			this.viewer.addEndAssetsLoadingListener(() => {
				this.isAssetsLoading = false;
				this.trigger('change');
			});

			if (this.highlightSettings) {
				this.viewer.highlightColor = this.highlightSettings.color;
				this.viewer.highlightSize = this.highlightSettings.size;
			}

			this.viewer.setLoadingScreen({
				displayLoadingUI: () => null,
				hideLoadingUI: () => null,
				loadingUIBackgroundColor: 'black',
				loadingUIText: 'black'
			});

			this.viewer.infoPointsManagerStyle = {
				imagePanelStyle: {
					backgroundColor: 'white',
					loadingTextColor: '#cccccc',
					buttonsBorderColor: '#dddddd',
					buttonsBorderHighlightColor: '#eeeeee',
					buttonsColor: '#888888',
					buttonsHighlightColor: '#555555',
					buttonsIconColor: 'white'
				}
			};

			// Set transparent background
			// this.zkScene.getOrCreateEnvironmentConfig().set('mainColor', '#000000');
			// this.zkScene.getOrCreateGeneralConfig().set('backgroundColor', '#000000');
			// this.zkScene.getOrCreateGeneralConfig().set('backgroundAlpha', 0);

			if (!(this.zkScene && this.zkScene.get('url') && this.zkScene!.get('url')!.endsWith('.glb')))
				// Pricing calculator
				this.pricingCalculator = await DesignPriceCalculatorRuleFactory.create(
					this.zkProduct,
					Zakeke.config.baseApiUrl,
					this.token,
					true,
					Zakeke.config.cloudFlareApiUrl
				);
			if (this.zkProduct!.get('colors').length > 0) {
				this.pricingCalculator.colorID = this.zkProduct!.get('colors').at(0).get('colorID');
				this.integration.setPricingCalculator(this.pricingCalculator);
			}
			if (this.zkScene && this.zkScene.get('url') && this.zkScene!.get('url')!.endsWith('.glb')) {
				await this.loadSceneFromGlbUrl(
					this.zkScene!.get('url')!,
					(this.zkScene!.get('config') as unknown as Zakeke.SceneConfig) ?? new Zakeke.SceneConfig({})
				);
			} else await this.viewer.loadSceneFromModelAsync(this.zkProduct, this.zkScene!);

			if (this.settings.brand) this.viewer.createBrandLogoButton();

			const tryOnSettings = this.zkScene?.getTryOnSettings?.();
			if (tryOnSettings?.typeSettings?.mandatoryPD) this.isPupillaryDistanceMandatory = true;

			this.trigger('change');

			await this.handleSceneLoaded();
		}
	};

	private buildDeepARSceneGLBUrl = (config: any) => {
		let finalUrl = Zakeke.config.deepARSettings.url + '?fileUrl=' + encodeURIComponent(this.zkScene!.get('url')!);
		let settings = this.tryOnMeshVisibleSettings || DefaultDeepARFineTuningSettings;
		let model = config.model;
		const queryParams = new URLSearchParams({
			positionX: settings!.position.x.toString(),
			positionY: settings!.position.y.toString(),
			positionZ: settings!.position.z.toString(),
			rotationX: config.model.rotation.x.toString(),
			rotationY: config.model.rotation.y.toString(),
			rotationZ: config.model.rotation.z.toString(),
			scale: settings.scale.toString(),
			environment: settings!.environment!.toString(),
			modelPositionX: model.position.x.toString(),
			modelPositionY: model.position.y.toString(),
			modelPositionZ: model.position.z.toString(),
			modelScale: model.scale.x.toString()
		});
		console.log('buildDeepARSceneGLBUrl con nuovi param model ', model.position.x, model.position.y, model.position.z, model.scale.x);

		finalUrl = `${finalUrl}&${queryParams.toString()}&isDesktopDeepARUrl=true`;
		console.log('sono finalUrl', finalUrl);
		return finalUrl;
	};

	public stopAllAnimations = () => {
		if (this.viewer) this.viewer.stopAllAnimations();
	};

	public hasAnimations = () => {
		if (this.viewer) return this.viewer.hasAnimations();
		return false;
	};

	public playAnimations = () => {
		if (this.viewer) this.viewer.playAnimations();
	};

	public loadSceneFromGlbUrl = async (glbUrl: string, sceneConfig: Zakeke.SceneConfig) => {
		const config = sceneConfig.toJSON();
		console.log(config, 'config scena');

		if (config.model) this.deepARsceneGLBUrl = this.buildDeepARSceneGLBUrl(config);
		await this.viewer.loadFromGlbUrl(glbUrl, config);
		if (this.zkProduct) this.viewer.replaceModel(this.zkProduct);
		if (this.zkScene) this.viewer.setZkScene(this.zkScene);
		if (this.isDAMViewer) {
			this.makeDAMAnalyticsView(DAMViewerAnalyticsEventType.ViewProduct);
		}
	};

	public appendViewer = (container: HTMLElement) => {
		if (this.viewer) this.viewer.appendTo(container);
		this.viewer.updateView(true, true);
	};

	/**
	 * Forces a resize of the 3D viewer canvas to match its container.
	 * Call this from the theme after all UI components have finished rendering
	 * to fix canvas buffer/display size mismatches caused by layout reflows.
	 */
	public forceViewerResize = () => {
		if (this.viewer) {
			this.viewer.updateView(true, true);
		}
	};

	public setHighlightSettings = (settings: { color: string; size: number }) => {
		this.highlightSettings = settings;

		if (this.viewer) {
			this.viewer.highlightColor = settings.color;
			this.viewer.highlightSize = settings.size;
		}
	};

	/*public renderDesign = _.throttle(async () => {
		if (this.viewer && this.zkCurrentTemplate) {
			await this.viewer.renderDesignAsync(this.zkCurrentTemplate);
			this.fireItemsChange();
		}
	}, 300);*/

	public renderDesign = async () => {
		console.log('Rendering design...', this.zkCurrentTemplate);
		if (this.viewer && this.zkCurrentTemplate) {
			await this.viewer.renderDesignAsync(this.zkCurrentTemplate!);
			this.debouncedChange();
		}
	};

	// Debounced 2D texture refresh. Replaces the 3D-core-side item listeners
	// (MeshDesignManager addItem*/addImageLoaded) that were disabled because they
	// updated too aggressively. Call after any 2D item mutation in this class so
	// rapid bursts (drag color picker, typing in text, batch ops) coalesce into one refresh.
	public debouncedRefresh = debounce(() => this.renderDesign(), 50);

	private updateDesignFast = debounce(async (guid: string) => {
		if (this.viewer) {
			await this.viewer.refreshDesignTextureForItem(guid);
		}
	}, 50);

	private debouncedChange = debounce(() => {
		this.trigger('change');
	}, 300);

	public restoreLastCameraLocation = () => {
		if (!this.viewer) return;

		//this.viewer.updateView(false);

		// Restore last camera location
		if (this.lastCameraLocation && this.lastCameraLocation !== Zakeke.getEmptyUUID()) {
			console.log('Restoring last camera location ' + this.lastCameraLocation + ' ...');
			this.setCamera(this.lastCameraLocation, false, false);
		} else {
			this.viewer.adjustCamera(false);
		}
	};

	private buildRecapProps = _.debounce((selectedOptions: SelectedOption[]) => {
		if (!this.sellerSettings || !this.sellerSettings.isCompositionRecapEnabled) return;
		const storeAttr = this.zkProduct!.get('attributes');
		const storeGroups = this.zkProduct!.get('attributeGroups');
		const storeSteps = this.zkProduct!.get('attributeSteps');
		const recapProps = {};

		selectedOptions.forEach((item: SelectedOption) => {
			const currentAttr = storeAttr.find((attr) => attr.get('attributeID') == item.attributeID);
			const currentOption = currentAttr!.get('options').find((opt) => opt.get('optionID') == item.optionID);
			const currentGroup = storeGroups.find((group) => group.get('groupID') == currentAttr!.get('groupID'));
			const currentStep = storeSteps.find((step) => step.get('stepID') == currentAttr!.get('stepID'));

			let label = '';
			let isOptionEnabled = false;

			this.selection?.get('groups').each((group) => {
				group.get('attributes').each((attr) => {
					const option = attr.get('options').find((x) => x.get('optionID') == currentOption!.get('optionID'));

					if (option && attr.get('enabled')) {
						isOptionEnabled = this.isOptionEnabled(currentOption!.get('optionID'));
						label =
							group.get('groupID') !== -1
								? this.T_dynamic(group.get('name')) + ' ' + this.T_dynamic(currentAttr!.get('name')!)
								: this.T_dynamic(currentAttr!.get('name')!);
						(recapProps as any)[`${label}`] = {
							groupDisplayOrder: currentGroup?.get('displayOrder') ?? 0,
							stepDisplayOrder: currentStep?.get('displayOrder') ?? 0,
							attributeDisplayOrder: currentAttr?.get('displayOrder') ?? 0,
							optionDisplayOrder: currentOption?.get('displayOrder') ?? 0,
							value: this.T_dynamic(currentOption!.get('name')!)
						};
					}
				});
			});
		});
		this.currentAttributesSelection = recapProps;
	}, 200);

	public updateRecapAndExtensionField = () => {
		const selectedOptions: SelectedOption[] = [];

		this.selection?.getAllAttributeSelections().forEach((attribute) => {
			attribute.get('options').forEach((option) => {
				if (option.get('selected')) {
					selectedOptions.push({
						attributeID: option.get('attributeID'),
						optionID: option.get('optionID')
					});
				}
			});
		});

		this.getCurrentExtensionFields(selectedOptions);
		this.buildRecapProps(selectedOptions);

		this.trigger('change');
	};

	private getCurrentExtensionFields = (options: any[]) => {
		const propsValues: any[] = [];
		const propsMap: any = {};
		const storeAttr = this.zkProduct!.get('attributes');

		options.forEach((item) => {
			const currentAttr = storeAttr.find((attr) => attr.get('attributeID') == item.attributeID);
			const currentOption = currentAttr!.get('options').find((opt) => opt.get('optionID') == item.optionID);

			if (currentOption!.get('extensionFields').length) {
				currentOption!.get('extensionFields').forEach((item: any) => {
					if (propsValues.length === 0 || propsValues.indexOf(item.name) == -1) {
						propsValues.push(item.name);
						propsMap[item.name] = {
							value: 0,
							label: item.label,
							formatString: item.formatString
						};
					}

					let val;

					if (item.dataTypeID === ExtensionFieldDataType.Integer) {
						val = parseInt(item.value);
					} else {
						val = parseFloat(item.value);
					}

					propsMap[item.name].value += val;
				});
			}
		});
		let itemsArray = Object.keys(propsMap).map(function (key) {
			return propsMap[key];
		});

		this.additionalCustomProperties = itemsArray;
	};

	private handleSceneLoaded = async () => {
		// window.addEventListener('resize', this.resize);

		console.log('Scene loaded');
		this.selection = Zakeke.ComposerSelection.CreateComposerSelection(this.zkProduct!);
		let currentCompositionId = this.templateId || this.draftCompositionDocId || this.sharedCompositionDocId || this.compositionId;

		if (this.initialSelection == undefined) {
			this.initialSelection = this.cloneSelection(this.selection);
		}

		await this.internalLoadComposition(currentCompositionId);

		this.trigger('change');
		this.buildStructure();

		// Trigger quantity update
		this.setQuantity(this.quantity);
		this.renderDesign();

		this.viewer.addNotifyMessageListener((areaID, itemGuid, type, title, message, closabled, requiredWidth, requiredHeight, addToCartDisabledIfVisible) => {
			this.visibleEventMessages.push({
				areaID,
				itemGuid,
				type,
				title,
				message,
				closabled,
				requiredWidth,
				requiredHeight,
				addToCartDisabledIfVisible
			});

			this.trigger('change');
		});
		this.viewer.addRemoveMessageListener((areaID, itemGuid) => {
			this.visibleEventMessages = this.visibleEventMessages.filter((x) => x.areaID !== areaID && x.itemGuid !== itemGuid);

			this.trigger('change');
		});

		this.viewer.setEventMessages(
			this.settings.eventMessages.map((x) => ({
				AddToCartDisabledIfVisible: x.addToCartDisabledIfVisible,
				AdditionalData: x.additionalData,
				Closeable: x.closeable,
				Description: x.description,
				EnableCheckForSellerImages: x.enableCheckForSellerImages,
				EventID: x.eventID,
				Visible: x.visible
			}))
		);

		if (this.sellerSettings) {
			this.addHotjarScript(this.sellerSettings?.hotjarSiteID);
			this.addGoogleTagManagerID(this.sellerSettings?.googleTagManagerID);
		}

		this.registerView();

		// recupero le compositions salvate se non sono in template editor e non è prodotto viewer
		if (!this.isPresetConfigurationEditor && !(this.zkScene && this.zkScene.get('url') && this.zkScene!.get('url')!.endsWith('.glb')))
			await this.updateSavedCompositions();

		setTimeout(() => this.updateRecapAndExtensionField(), 1000);
	};

	private getComposition = async (id: string) => {
		return new Promise<MPlaza.Composition>((resolve, reject) => {
			const composition = new MPlaza.Composition();
			composition.set('docID', id);
			composition.fetch({
				success: (model) => {
					resolve(model);
				},
				error: () => {
					reject();
				},
				data: {
					frombackoffice: this.isDraftEditor ? true : false
				}
			});
		});
	};

	private getDesign = async (id: string, isTemplate?: boolean) => {
		console.log('sono get design con id e isTemplate opzionale', id);
		return new Promise<MPlaza.Design>((resolve, reject) => {
			const design = new MPlaza.Design();

			design.set('docID', id);
			design.fetch({
				success: (model) => {
					resolve(model);
				},
				error: () => {
					reject();
				},
				data: {
					frombackoffice: this.isDraftEditor ? true : false,
					...(isTemplate !== undefined && { template: isTemplate }) // Pass isTemplate only if defined
				}
			});
			design.addEventListener('change', this.updatePrice);
		});
	};

	private internalLoadDesign = async (id: string, isTemplate?: boolean) => {
		this.zkCurrentTemplate = await this.getDesign(id, isTemplate);
		this.currentTemplate = Template.fromTemplate(this.zkCurrentTemplate);
		await downloadFontsOfDesign(this.zkCurrentTemplate, this.fonts);
		this.renderDesign();
	};

	private addHotjarScript(siteID: string | null) {
		if (siteID && siteID.length > 0 && this.platform && this.platform.toLowerCase() !== 'preview') {
			var script = document.createElement('script');
			script.type = 'text/javascript';
			script.innerHTML = `(function(h,o,t,j,a,r){
                    h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
                    h._hjSettings={hjid:${siteID},hjsv:6};
                    a=o.getElementsByTagName('head')[0];
                    r=o.createElement('script');r.async=1;
                    r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
                    a.appendChild(r);
                })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');`;
			document.getElementsByTagName('head')[0].appendChild(script);
		}
	}

	private addGoogleTagManagerID(siteID: string | null) {
		if (siteID && siteID.length > 0 && this.platform && this.platform.toLowerCase() !== 'preview') {
			const script = document.createElement('script');
			script.textContent = `(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
				new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
				j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
				'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
				})(window,document,'script','dataLayer','${siteID}')`;
			document.head.appendChild(script);

			var noscript = document.createElement('noscript');

			var iframe = document.createElement('iframe');
			iframe.src = `https://www.googletagmanager.com/ns.html?id=${siteID}`;
			iframe.height = '0';
			iframe.width = '0';
			iframe.style.cssText = 'display:none;visibility:hidden';
			noscript.appendChild(iframe);
			document.body.insertBefore(noscript, document.body.firstChild);
		}
	}

	public loadComposition = async (id: string | null) => {
		await this.internalLoadComposition(id);
		this.buildStructure();
	};

	private internalLoadComposition = async (id: string | null) => {
		// Get or create a new composition
		if (id) {
			this.composition = await this.getComposition(id);
			if (this.templateId) {
				this.loadedComposition = {
					templateName: this.composition.get('name'),
					attributesOptions: this.composition.get('compositionItems').map((compositionItem: MPlaza.CompositionItem) => {
						return {
							attributeID: compositionItem.get('attributeID'),
							optionID: compositionItem.get('selectedOptionID')
						};
					}),
					selectedCategoryID: this.composition.get('categories').length > 0 ? this.composition.get('categories').at(0).get('categoryID') : null
				};
			}

			this.selection.setupOnComposition(this.composition);

			await this.sync3DAndPlatformSelectionWithCurrentSelection();

			this.updateRecapAndExtensionField();
			this.updateComposition();

			setTimeout(async () => {
				// nel caso dei template salviamo un eventuale design sul designID, quindi
				// carico il design da quello (che poi coincide col designDocID)
				console.log('inizio setTimeout per caricare design in composition', this.composition);
				if (this.composition.get('isTemplate') && this.composition.get('designID') && this.composition.get('designID') !== -1) {
					this.shouldLoadDesign = true;
					console.log('sei nella composition template con un designID', this.composition.get('designID'));
					await this.internalLoadDesign(this.composition.get('designID').toString());
				} else if (this.composition.get('designDocID')) {
					console.log('sono prima di internalLoadDesign in composition con designDocID', this.composition.get('designDocID'));
					await this.internalLoadDesign(this.composition.get('designDocID'));

					// Azzeriamo i campi per le shared composition dopo aver caricato il design
					if (this.sharedCompositionDocId) {
						this.composition.set('docID', null);
						this.composition.set('designDocID', null);
					}
				} else if (this.templates.length > 0) {
					await this.setTemplate(this.templates[0].id);
				}
				// faccio partire il flag da null in modo da aspettare il check in questo timeout
				// per capire se devo aspettare il load del design del template oppure far caricare, lato UI,
				// il primo predesign template disponibile
				if (
					!this.composition.get('isTemplate') ||
					(this.composition.get('isTemplate') && (!this.composition.get('designID') || this.composition.get('designID') === -1))
				) {
					this.shouldLoadDesign = false;
				}

				if (this.sharedCompositionDocId) {
					this.composition.set('docID', null);
					this.composition.set('designDocID', null);
				}
			}, 1000);
		} else {
			this.composition = this.selection.buildComposition();
			// Select initial values from platform
			for (let platformSelection of this.platformAttributesSelection) {
				// Search the attribute
				this.zkProduct!.get('attributes').each((attribute) => {
					if (isPlatformCode(attribute.get('attributeCode')!)) {
						try {
							if (compare(JSON.parse(attribute.get('attributeCode')!), JSON.parse(platformSelection.attributeCode))) {
								let option = attribute.get('options').find((option) => {
									return compare(JSON.parse(option.get('optionCode')!), JSON.parse(platformSelection.optionCode));
								});

								if (option) {
									this.selection.selectAttributeValue(attribute.get('attributeID'), option.get('optionID').toString());

									// Update here the labels because events are not attached yet (attached below)
									platformSelection.attributeLabel = this.T_dynamic(attribute.get('name')!);
									platformSelection.optionLabel = this.T_dynamic(option.get('name')!);
								}
							}
						} catch (ex) {
							console.error('Failed parsing attribute from data:', platformSelection);
							console.error(ex);
						}
					}
				});
			}

			if (this.templates.length > 0) await this.setTemplate(this.templates[0].id);

			await this.sync3DAndPlatformSelectionWithCurrentSelection();

			this.updateComposition();
			this.updateRecapAndExtensionField();
		}
	};

	public sync3DAndPlatformSelectionWithCurrentSelection = async () => {
		// Add all composition attributes to the platformAttributes that we will send to the plugin
		this.selection.get('groups').each((group) => {
			group.get('attributes').each((attribute) => {
				// Here we check if this attributes was already passed from the parameters.
				// If yes, we ignore it because the correct value was already selected in the previous rows.
				// else we add it to the list of attributes selections

				let oldAttribute = this.platformAttributesSelection.find((x) => {
					return matchCodes(x.attributeCode, attribute.get('attributeCode'));
				});

				let selectedOption = attribute.get('options').findWhere({ selected: true });
				if (!oldAttribute) {
					if (selectedOption)
						this.platformAttributesSelection.push(
							new PlatformAttributeSelection(
								attribute.get('attributeCode'),
								selectedOption.get('optionCode'),
								this.T_dynamic(attribute.get('name')),
								this.T_dynamic(selectedOption.get('name')),
								attribute.get('enabled'),
								selectedOption.get('enabled')
							)
						);
				} else if (selectedOption) {
					// Update existing platform attribute to match current selection
					// (e.g. when a shared composition has a different option than boot params)
					oldAttribute.optionCode = selectedOption.get('optionCode')!;
					oldAttribute.optionLabel = this.T_dynamic(selectedOption.get('name')!);
					oldAttribute.attributeLabel = this.T_dynamic(attribute.get('name')!);
					oldAttribute.attributeIsEnabled = attribute.get('enabled');
					oldAttribute.optionIsEnabled = selectedOption.get('enabled');
				}
			});
		});

		const script = this.selection.getAllAttributeTransformationScript();
		this.viewer.adjustCamera(false);

		await this.viewer.executeScriptAsync(script);

		this.selection.on('attributeSelectionChanged', (e: any) => {
			this.handleAttributeSelectionChanged(e);
		});
		this.selection.on('attributeEnabled', (e: any) => {
			this.handleAttributeEnabled(e.attributeID);
		});
		this.selection.on('attributeDisabled', (e: any) => {
			this.handleAttributeDisabled(e.attributeID);
		});
		this.selection.on('attributeOptionEnabled', (e: any) => {
			this.handleAttributeOptionEnabled(e.attributeID, e.optionID);
		});
		this.selection.on('attributeOptionDisabled', (e: any) => {
			this.handleAttributeOptionDisabled(e.attributeID, e.optionID);
		});
	};

	private cloneSelection = (selection: Zakeke.ComposerSelection) => {
		const clone = new Zakeke.ComposerSelection();
		(clone as any).set('zkModel', this.zkProduct);
		(clone as any).set('attributeDependencies', selection.get('attributeDependencies'));
		(clone as any).set('optionDependencies', selection.get('optionDependencies'));

		clone.get('groups').reset();
		selection.get('groups').each((group) => {
			var cloneGroup = new Zakeke.AttributeGroupSelection();
			for (const key in group.attributes) {
				(cloneGroup as any).set(key, group.attributes[key]);
			}
			clone.get('groups').add(cloneGroup);
		});

		let idx = 0;
		clone.get('groups').each((group) => {
			(group as any).set('attributes', selection.get('groups').at(idx).get('attributes').clone());

			group.get('attributes').reset();
			selection
				.get('groups')
				.at(idx)
				.get('attributes')
				.each((attribute) => {
					var cloneAttribute = new Zakeke.AttributeSelection();
					for (const key in attribute.attributes) {
						(cloneAttribute as any).set(key, attribute.attributes[key]);
					}

					group.get('attributes').add(cloneAttribute);
				});

			let idx2 = 0;
			group.get('attributes').each((attribute) => {
				(attribute as any).set('options', selection.get('groups').at(idx).get('attributes').at(idx2).get('options').clone());

				attribute.get('options').reset();
				selection
					.get('groups')
					.at(idx)
					.get('attributes')
					.at(idx2)
					.get('options')
					.each((option) => {
						var cloneOption = new Zakeke.OptionSelection();
						for (const key in option.attributes) {
							(cloneOption as any).set(key, option.attributes[key]);
						}

						attribute.get('options').add(cloneOption);
					});

				idx2++;
			});

			idx++;
		});

		return clone;
	};

	public undo = async () => {
		let selection = this.undoStack && this.undoStack.length > 0 ? this.undoStack.pop() : null;

		if (!selection) return;

		this.redoStack.push(this.selection);
		this.selection = selection;

		this.selection.on('attributeSelectionChanged', (e: any) => {
			this.handleAttributeSelectionChanged(e);
		});
		this.selection.on('attributeEnabled', (e: any) => {
			this.handleAttributeEnabled(e.attributeID);
		});
		this.selection.on('attributeDisabled', (e: any) => {
			this.handleAttributeDisabled(e.attributeID);
		});
		this.selection.on('attributeOptionEnabled', (e: any) => {
			this.handleAttributeOptionEnabled(e.attributeID, e.optionID);
		});
		this.selection.on('attributeOptionDisabled', (e: any) => {
			this.handleAttributeOptionDisabled(e.attributeID, e.optionID);
		});

		this.selection.attachEvents();

		let script = this.selection.getAllAttributeTransformationScript();
		await this.viewer.executeScriptAsync(script);

		this.buildStructure();
		this.resetPlatformAttributesSelection();
		this.updateComposition();
		this.updateRecapAndExtensionField();
	};

	public redo = async () => {
		let selection = this.redoStack && this.redoStack.length > 0 ? this.redoStack.pop() : null;

		if (!selection) return;

		this.undoStack.push(this.selection);
		this.selection = selection;

		this.selection.on('attributeSelectionChanged', (e: any) => {
			this.handleAttributeSelectionChanged(e);
		});
		this.selection.on('attributeEnabled', (e: any) => {
			this.handleAttributeEnabled(e.attributeID);
		});
		this.selection.on('attributeDisabled', (e: any) => {
			this.handleAttributeDisabled(e.attributeID);
		});
		this.selection.on('attributeOptionEnabled', (e: any) => {
			this.handleAttributeOptionEnabled(e.attributeID, e.optionID);
		});
		this.selection.on('attributeOptionDisabled', (e: any) => {
			this.handleAttributeOptionDisabled(e.attributeID, e.optionID);
		});

		this.selection.attachEvents();

		let script = this.selection.getAllAttributeTransformationScript();
		await this.viewer.executeScriptAsync(script);

		this.buildStructure();
		this.resetPlatformAttributesSelection();
		this.updateComposition();
		this.updateRecapAndExtensionField();
	};
	/**
	 * Reset the platform attributes selection to the current selection
	 */
	private resetPlatformAttributesSelection = () => {
		this.selection.get('groups').each((group) => {
			group.get('attributes').each((attribute) => {
				let resetAttribute = this.platformAttributesSelection.find((x) => {
					return matchCodes(x.attributeCode, attribute.get('attributeCode'));
				});

				if (resetAttribute) {
					let selectedOption = attribute.get('options').findWhere({ selected: true });
					resetAttribute.attributeCode = attribute.get('attributeCode')!;
					resetAttribute.attributeIsEnabled = attribute.get('enabled');
					resetAttribute.attributeLabel = this.T_dynamic(attribute.get('name')!);
					resetAttribute.optionCode = selectedOption!.get('optionCode')!;
					resetAttribute.optionLabel = this.T_dynamic(selectedOption!.get('name')!);
					resetAttribute.optionIsEnabled = selectedOption!.get('enabled');
				}
			});
		});
	};

	public reset = async () => {
		if (!this.initialSelection) return;

		this.undoStack = [];
		this.redoStack = [];

		this.selection = this.cloneSelection(this.initialSelection);

		this.selection.attachEvents();

		this.selection.on('attributeSelectionChanged', (e: any) => {
			this.handleAttributeSelectionChanged(e);
		});

		this.selection.on('attributeEnabled', (e: any) => {
			this.handleAttributeEnabled(e.attributeID);
		});

		this.selection.on('attributeDisabled', (e: any) => {
			this.handleAttributeDisabled(e.attributeID);
		});

		this.selection.on('attributeOptionEnabled', (e: any) => {
			this.handleAttributeOptionEnabled(e.attributeID, e.optionID);
		});

		this.selection.on('attributeOptionDisabled', (e: any) => {
			this.handleAttributeOptionDisabled(e.attributeID, e.optionID);
		});

		let script = this.selection.getAllAttributeTransformationScript();
		await this.viewer.executeScriptAsync(script);

		this.buildStructure();
		this.resetPlatformAttributesSelection();
		this.updateComposition();
		this.updateRecapAndExtensionField();
	};

	private handleAttributeSelectionChanged = async (e: any) => {
		// Filter and log material parameter actions specifically
		const materialParameterActions = e.actions.filter(
			(action) => action.actionType === 'set material parameter' || action.actionTypeID === 3 // Assuming 3 is the ID for material parameter actions
		);

		if (materialParameterActions.length > 0) {
			console.log('=== SET MATERIAL PARAMETER ACTIONS ===');
			materialParameterActions.forEach((action, index) => {
				console.log(`Material Parameter Action ${index + 1}:`, {
					actionType: action.actionType,
					actionTypeID: action.actionTypeID,
					materialName: action.materialName,
					parameterName: action.parameterName,
					parameterValue: action.parameterValue,
					targetMeshes: action.targetMeshes,
					fullAction: action
				});
			});
		}

		await this.viewer.executeActionsAsync(e.attributeID, e.selectedValue, e.actions);

		this.updateComposition();

		const attribute = this.zkProduct!.get('attributes').find((attribute) => attribute.get('attributeID') == e.attributeID);

		if (attribute) {
			const option = attribute.get('options').find((option) => option.get('optionID') == e.selectedValue);

			if (option) {
				// Change platform selection
				let platformSelection = this.platformAttributesSelection.find((x) => {
					return matchCodes(x.attributeCode, attribute.get('attributeCode')!);
				});

				if (platformSelection) {
					platformSelection.optionCode = option.get('optionCode')!;
					platformSelection.optionLabel = this.T_dynamic(option.get('name')!);
				}

				this.setOptionSelectionEnabled(attribute.get('attributeID'), option.get('optionID'), this.isOptionEnabled(option.get('optionID')));
			}
		}

		this.buildStructure();
		this.renderDesign();
	};

	private handleAttributeEnabled = (attributeId: number) => {
		this.updateComposition();
		this.setAttributeSelectionEnabled(attributeId, true);
		this.buildStructure();
		this.renderDesign();
	};

	private handleAttributeDisabled = (attributeId: number) => {
		this.updateComposition();
		this.setAttributeSelectionEnabled(attributeId, false);
		this.buildStructure();
		this.renderDesign();
	};

	private handleAttributeOptionEnabled = (attributeId: number, optionId: number) => {
		this.updateComposition();
		this.setOptionSelectionEnabled(attributeId, optionId, true);
		this.buildStructure();
		this.renderDesign();
	};

	private handleAttributeOptionDisabled = (attributeId: number, optionId: number) => {
		this.updateComposition();
		this.setOptionSelectionEnabled(attributeId, optionId, false);
		this.buildStructure();
		this.renderDesign();
	};

	// Set the current attribute platform selection as enabled or not
	private setAttributeSelectionEnabled = (attributeId: number, isEnabled: boolean) => {
		let attribute = this.zkProduct!.get('attributes').find((attribute) => attribute.get('attributeID') == attributeId);

		if (attribute) {
			let attributePlatformSelection = this.platformAttributesSelection.find((selection) => {
				return matchCodes(selection.attributeCode, attribute!.get('attributeCode')!);
			});

			if (attributePlatformSelection) {
				attributePlatformSelection.attributeIsEnabled = isEnabled;
			}

			// Set all options as disabled too in platfrom attributes
			attribute.get('options').each((option) => {
				this.setOptionSelectionEnabled(attributeId, option.get('optionID'), isEnabled);
			});
		}
	};

	// Set the current option platform selection as enabled or not but only if it's the selected option
	private setOptionSelectionEnabled = (attributeId: number, optionId: number, isEnabled: boolean) => {
		let attribute = this.zkProduct!.get('attributes').find((attribute) => attribute.get('attributeID') == attributeId);

		if (attribute) {
			let option = attribute.get('options').find((option) => option.get('optionID') == optionId);

			if (option) {
				let attributePlatformSelection = this.platformAttributesSelection.find((selection) => {
					return matchCodes(selection.attributeCode, attribute!.get('attributeCode')!);
				});

				if (attributePlatformSelection && matchCodes(attributePlatformSelection.optionCode, option.get('optionCode')!)) {
					attributePlatformSelection.optionIsEnabled = isEnabled;
				}
			}
		}
		this.updateRecapAndExtensionField();
	};

	private isOptionEnabled = (optionId: number) => {
		let enabled = false;

		this.selection.get('groups').each((group) => {
			group.get('attributes').each((attr) => {
				let option = attr.get('options').find((x) => x.get('optionID') == optionId);
				if (option) enabled = option.get('enabled');
			});
		});

		return enabled;
	};

	private updateComposition = () => {
		this.selection.updateComposition(this.composition);
		this.updatePrice();
	};

	public setQuantity(quantity: number) {
		// Adapt quantity to rules
		let quantityRule = this.zkProduct!.get('quantityRule');
		if (quantityRule) {
			const min = quantityRule.get('minQuantity');
			const max = quantityRule.get('maxQuantity');
			const step = quantityRule.get('step');

			if (min && quantity < min) quantity = min;

			if (max && quantity > max) quantity = max;

			if (step) {
				quantity = Math.round(quantity / step) * step;

				if (quantity < step) quantity = step;
			}
		}

		this.quantity = quantity;
		this.trigger('change');
		this.updatePrice();
	}

	private _updatePrice = async () => {
		let price = 0;
		let isOutOfStock = false;
		if (this.composition && this.zkProduct) {
			const result = await this.getPrice();

			price = result.price;
			isOutOfStock = result.isOutOfStock;
		}

		this.price = price;
		this.isOutOfStock = isOutOfStock;
		this.trigger('change');
	};

	private updatePrice = _.debounce(this._updatePrice, 300);

	private buildStructure = () => {
		let data: Group[] = [];
		if (this.selection) {
			for (var i = 0; i < this.selection.get('groups').length; i++) {
				var group = this.selection.get('groups').at(i);
				var groupData = this.createGroupFromSelection(group);
				data.push(groupData);
			}

			data.sort((a, b) => a.displayOrder - b.displayOrder);
		}
		this.groups = data;
		this.trigger('change');
	};

	private createGroupFromSelection = (groupSelection: Zakeke.AttributeGroupSelection): Group => {
		let zakekeGroup = this.zkProduct!.get('attributeGroups').get(groupSelection.get('groupID'));
		let imageUrl = zakekeGroup ? zakekeGroup.get('imageUrl') : null;

		let group: Group = new Group();

		group.id = zakekeGroup ? zakekeGroup.get('groupID') : -1;
		group.guid = zakekeGroup ? zakekeGroup.get('groupGuid') : '-1';
		group.name = zakekeGroup ? zakekeGroup.get('name')! : 'Other';
		group.imageUrl = imageUrl;
		group.enabled = true;
		group.cameraLocationId = zakekeGroup ? zakekeGroup.get('sceneCameraLocationID')! : null;
		group.steps = this.zkProduct!.get('attributeSteps')
			.filter((step) => step.get('groupID') == group.id)
			.map((step) => this.createStep(step, groupSelection));

		if (zakekeGroup) {
			group.displayOrder = zakekeGroup.get('displayOrder');
			group.direction = zakekeGroup.get('direction');
			group.attributesAlwaysOpened = zakekeGroup.get('attributesAlwaysOpened');
		}

		for (var i = 0; i < groupSelection.get('attributes').length; i++) {
			let attributeSelection = groupSelection.get('attributes').at(i);
			let zakekeAttribute = this.zkProduct!.get('attributes').find((attribute) => attribute.get('attributeID') == attributeSelection.get('attributeID'))!;

			if (zakekeAttribute.get('stepID') == -1) {
				let attribute = this.createAttributeFromSelection(attributeSelection);
				group.attributes.push(attribute);
			}
		}

		group.attributes.sort((a, b) => a.displayOrder - b.displayOrder);

		// template groups

		const templateGroups = groupSelection?.get('templateGroups');
		if (templateGroups) {
			for (var k = 0; k < this.zkProduct!.get('templateGroups').length; k++) {
				let zakekeTemplateGroup = this.zkProduct!.get('templateGroups').at(k);
				let templateGroupSelection = templateGroups.find((templGr) => templGr.get('templateGroupID') == zakekeTemplateGroup.get('templateGroupID'));

				if (templateGroupSelection && zakekeTemplateGroup.get('stepID') == null) {
					let templateGroup = this.createTemplateGroupFromSelection(templateGroupSelection);
					group.templateGroups.push(templateGroup);
				}
			}
		}

		group.templateGroups.sort((a, b) => a.displayOrder - b.displayOrder);

		return group;
	};

	private createStep(zkStep: MPlaza.AttributeStep, groupSelection: Zakeke.AttributeGroupSelection) {
		let step: Step = new Step();
		step.id = zkStep.get('stepID');
		step.guid = zkStep.get('stepGuid');
		step.name = zkStep.get('name') ?? '';
		step.cameraLocationID = zkStep.get('cameraLocationID') ?? null;
		step.displayOrder = zkStep.get('displayOrder');

		// The attributes in a step must have the group ID too or the group selection will be empty
		for (var i = 0; i < this.zkProduct!.get('attributes').length; i++) {
			let zakekeAttribute = this.zkProduct!.get('attributes').at(i);
			let attributeSelection = groupSelection.get('attributes').find((attr) => attr.get('attributeID') == zakekeAttribute.get('attributeID'));

			if (attributeSelection && zakekeAttribute.get('stepID') == zkStep.get('stepID')) {
				let attribute = this.createAttributeFromSelection(attributeSelection);
				step.attributes.push(attribute);
			}
		}

		step.attributes.sort((a, b) => a.displayOrder - b.displayOrder);

		// Template groups
		for (var k = 0; k < this.zkProduct!.get('templateGroups').length; k++) {
			let zakekeTemplateGroup = this.zkProduct!.get('templateGroups').at(k);
			let templateGroupSelection = groupSelection
				.get('templateGroups')
				.find((templGr) => templGr.get('templateGroupID') == zakekeTemplateGroup.get('templateGroupID'));

			if (templateGroupSelection && zakekeTemplateGroup.get('stepID') == zkStep.get('stepID')) {
				let templateGroup = this.createTemplateGroupFromSelection(templateGroupSelection);
				step.templateGroups.push(templateGroup);
			}
		}

		step.templateGroups.sort((a, b) => a.displayOrder - b.displayOrder);

		return step;
	}

	private createAttributeFromSelection = (attributeSelection: any): Attribute => {
		let zakekeAttribute = this.zkProduct!.get('attributes').get(attributeSelection.get('attributeID'));

		var attribute: Attribute = new Attribute();

		attribute.id = zakekeAttribute.get('attributeID');
		attribute.guid = zakekeAttribute.get('attributeGuid');
		attribute.code = zakekeAttribute.get('attributeCode')!;
		attribute.name = zakekeAttribute.get('name') || '';
		attribute.description = zakekeAttribute.get('description') || '';
		attribute.optionShapeType = zakekeAttribute.get('optionPreviewShapeID') == 1 ? AttributeOptionShapeType.Round : AttributeOptionShapeType.Square;
		attribute.displayOrder = zakekeAttribute.get('displayOrder');
		attribute.enabled = attributeSelection.get('enabled');
		attribute.groupId = zakekeAttribute.get('groupID');
		attribute.stepId = zakekeAttribute.get('stepID');
		attribute.hideOptionsLabel = zakekeAttribute.get('hideOptionsLabel');
		attribute.cameraLocationId = zakekeAttribute ? zakekeAttribute.get('sceneCameraLocationID')! : null;

		// Options
		for (var i = 0; i < attributeSelection.get('options').length; i++) {
			let optionSelection = attributeSelection.get('options').at(i);
			let option = this.createOptionFromSelection(optionSelection);
			option.attribute = attribute;

			attribute.options.push(option);
		}

		if (attributeSelection.get('attributeTypeID') == MPlaza.AttributeType.Options) {
			attribute.type = AttributeType.Select;
		} else if (attributeSelection.get('attributeTypeID') == MPlaza.AttributeType.Color) {
			attribute.type = AttributeType.Colorpicker;
			attribute.value = attributeSelection.get('selectedValue');
		} else if (attributeSelection.get('attributeTypeID') == MPlaza.AttributeType.Number) {
			attribute.type = AttributeType.Colorpicker;
			attribute.value = attributeSelection.get('selectedValue');
			attribute.numberFormat = '';
		} else if (attributeSelection.get('attributeTypeID') == MPlaza.AttributeType.String) {
			attribute.type = AttributeType.Colorpicker;
			attribute.value = attributeSelection.get('selectedValue');
			attribute.numberFormat = '';
		}

		attribute.options.sort((a, b) => a.displayOrder - b.displayOrder);

		return attribute;
	};

	private createOptionFromSelection = (optionSelection: any): Option => {
		var zakekeOption = this.zkProduct!.get('attributes').get(optionSelection.get('attributeID')).get('options').get(optionSelection.get('optionID'));
		var option: Option = new Option();
		var pricings = this.zkProduct!.get('attributes').get(optionSelection.get('attributeID')).get('pricings');
		if (pricings.length > 0) {
			var optionPrice = pricings.find((price) => price.get('selectedOptionID') === optionSelection.get('optionID'))?.get('price');
			if (optionPrice) option.pricings = [{ fromQuantity: 0, price: optionPrice }];
		}
		option.id = zakekeOption.get('optionID');
		option.guid = zakekeOption.get('optionGuid');
		option.code = zakekeOption.get('optionCode')!;
		option.name = zakekeOption.get('name') || '';
		option.description = zakekeOption.get('description') || '';
		option.imageUrl = zakekeOption.get('imageUrl');
		option.displayOrder = zakekeOption.get('displayOrder');
		option.selected = optionSelection.get('selected');
		option.enabled = optionSelection.get('enabled');
		return option;
	};

	private createTemplateGroupFromSelection = (templateGroupSelection: any): ThemeTemplateGroup => {
		let zakekeTemplateGroup = this.zkProduct!.get('templateGroups').find(
			(templateGroup) => templateGroup.get('templateGroupID') === templateGroupSelection.get('templateGroupID')
		);
		if (!zakekeTemplateGroup) return new ThemeTemplateGroup();
		var templateGroup: ThemeTemplateGroup = new ThemeTemplateGroup();
		templateGroup.templateGroupID = zakekeTemplateGroup.get('templateGroupID');
		templateGroup.name = zakekeTemplateGroup.get('name') ?? '';
		templateGroup.groupID = zakekeTemplateGroup.get('groupID');
		templateGroup.stepID = zakekeTemplateGroup.get('stepID');
		templateGroup.displayOrder = zakekeTemplateGroup.get('displayOrder');

		for (var i = 0; i < templateGroupSelection.get('macrocategory').length; i++) {
			let macroCategorySelection = templateGroupSelection.get('macrocategory').at(i);
			let zakekeMacroCategory = zakekeTemplateGroup
				.get('macrocategory')
				.find((macroCategory: MPlaza.FrontEndMacroCategory) => macroCategory.get('macroCategoryID') === macroCategorySelection.get('macroCategoryID'));
			if (!zakekeMacroCategory) return templateGroup;

			let macroCategory: ThemeMacroCategory = new ThemeMacroCategory();
			macroCategory.id = zakekeMacroCategory.get('macroCategoryID');
			macroCategory.name = zakekeMacroCategory.get('name')!;

			for (var j = 0; j < macroCategorySelection.get('category').length; j++) {
				let categorySelection = macroCategorySelection.get('category').at(j);
				let zakekeCategory = zakekeMacroCategory.get('category').find((category) => category.get('categoryID') === categorySelection.get('categoryID'));
				if (!zakekeCategory) return templateGroup;

				let category: ThemeCategory = new ThemeCategory();
				category.id = zakekeCategory.get('categoryID');
				category.name = zakekeCategory.get('name')!;

				for (var k = 0; k < categorySelection.get('composition').length; k++) {
					let compositionSelection = categorySelection.get('composition').at(k);
					let zakekeComposition = zakekeCategory
						.get('composition')
						.find((composition) => composition.get('compositionID') === compositionSelection.get('compositionID'));
					if (!zakekeComposition) return templateGroup;

					let composition: TemplateComposition = new TemplateComposition();
					composition.name = zakekeComposition.get('name')!;
					composition.previewImageUrl = zakekeComposition.get('previewImageUrl');
					composition.compositionID = zakekeComposition.get('compositionID');

					category.composition.push(composition);
				}

				macroCategory.category.push(category);
			}

			templateGroup.macroCategory.push(macroCategory);
		}
		return templateGroup;
	};

	private saveDesignBackbone = async (isSharing?: boolean, compositionID?: number) => {
		return new Promise<void>((resolve, reject) => {
			let design = this.zkCurrentTemplate;

			if (this.isPresetConfigurationEditor) {
				console.log('i template con true vengono recuperati nel bo , ad ogni modo qui lo setto a true');
				design?.set('isTemplate', true);
			}
			// qui è quando risalvo il design la seconda volta, avendo la compositionID
			// questo mi permetterà di distinguere i design che vengono salvati da una preset configuration editor
			// da quelli che vengono salvati in composer
			if (compositionID) {
				console.log('salvo design con compositionID', compositionID);
				design?.set('compositionID', compositionID);
			}

			if (!design) {
				resolve();
				return;
			}
			design.save(null, {
				success: (model) => {
					if (!compositionID) {
						console.log('saved design with id', model.get('docID'));
						if (this.isPresetConfigurationEditor) this.composition.set('designID', model.get('designID'));
						else this.composition.set('designDocID', model.get('docID'));
					}
					resolve();
				},
				error: () => {
					reject();
				},
				headers: {
					fromBackOffice: this.isDraftEditor ? true : false,
					isSharing: isSharing ? true : false
				}
			});
		});
	};

	private saveCompositionBackbone = async (saveDesignFromTemplateEditor?: boolean, isSharing?: boolean) => {
		return new Promise<void>((resolve, reject) => {
			this.composition.save(null, {
				success: async () => {
					// se siamo in preset configuration editor e hai bisogno di salvare il design,
					// arrivati qui lo avrai già salvato quindi lo risalvi aggiungendo la compositionID
					// che ti permetterà di distinguere i design che vengono salvati da una preset configuration editor
					// da quelli che vengono salvati in composer
					if (this.isPresetConfigurationEditor && saveDesignFromTemplateEditor)
						await this.saveDesignBackbone(isSharing, this.composition.get('compositionID'));

					resolve();
				},
				error: () => {
					reject();
				},
				headers: {
					fromBackOffice: this.isDraftEditor ? true : false
				}
			});
		});
	};

	// Events

	private trigger = (event: string, ...params: any[]) => {
		for (let handler of this.events.filter((x) => x.event == event)) {
			if (handler.callback) handler.callback.apply(this, params);
			else console.error("Event handler can't be null or undefined");
		}
	};

	public on = (event: string, callback: any) => {
		this.events.push(new ZakekeEvent(event, callback));
	};

	public off = (event: string, callback: any) => {
		this.events = this.events.filter((x) => {
			return x.event == event && x.callback == callback;
		});
	};

	public setTemplate = async (templateId: number) => {
		this.zkCurrentTemplate = this.zkTemplates.find((x) => x.get('templateDesignID') == templateId);
		this.currentTemplate = this.templates.find((x) => x.id == templateId) || null;
		await downloadFontsOfDesign(this.zkCurrentTemplate!, this.fonts);
		this.renderDesign();
	};

	public getTemplateUploadRestrictions = (areaId: number) => {
		const design = this.zkCurrentTemplate;

		if (!design) throw 'could not read applied design ';

		const designSide = design.getDesignSide(this.zkProduct!.get('colors').at(0).get('sides').get(areaId));

		const getUploadRestriction = (restriction: string) => {
			if (designSide && designSide.get('uploadRestrictions'))
				return designSide.get('uploadRestrictions')?.[restriction as keyof MPlaza.UploadRestrictions];
			else if (design.get('uploadRestrictions')) design.get('uploadRestrictions')?.[restriction as keyof MPlaza.UploadRestrictions];
			else return null;
		};

		const pdfAddonEnabled =
			(this.settings.addons.find((x) => x.addonName === 'PDF' || x.addonName === 'PDF_EPS') || this.settings.isPdfEpsEnabled) ?? false;
		const epsAddonEnabled =
			(this.settings.addons.find((x) => x.addonName === 'EPS' || x.addonName === 'PDF_EPS') || this.settings.isPdfEpsEnabled) ?? false;
		return {
			isUserImageAllowed: getUploadRestriction('isUserImageAllowed') ?? true,
			isJpgAllowed: getUploadRestriction('isJpgAllowed') ?? true,
			isPngAllowed: getUploadRestriction('isPngAllowed') ?? true,
			isSvgAllowed: getUploadRestriction('isSvgAllowed') ?? true,
			isPdfAllowed: (getUploadRestriction('isPdfAllowed') ?? true) && !!pdfAddonEnabled,
			isEpsAllowed: (getUploadRestriction('isEpsAllowed') ?? true) && !!epsAddonEnabled,
			isPdfWithRasterAllowed: (getUploadRestriction('isPdfWithRasterAllowed') ?? true) && !!pdfAddonEnabled
		};
	};

	// Get template
	private getTemplates = async () => {
		return new Promise<any>(async (resolve, reject) => {
			// prendo i template tranne quelli creati nel preset configuration editor,
			// poi se seono nel preset configuration editor evito cache, altrimenti lato front true
			const cache = this.isPresetConfigurationEditor ? false : true;
			const fromComposer = true;
			let templatesList = (await axiosApi.get(`designs/templates/${this.zkProduct!.get('modelID')}?cache=${cache}&fromComposer=${fromComposer}`)).data;
			let templates: any[] = [];

			// Build a new design
			if (templatesList.length > 0) {
				for (const _template of templatesList[0].categories[0].templates) {
					const template = await this.getTemplate(_template.designID);
					//await downloadFontsOfDesign(template, this.fonts);
					templates.push(template);
				}
			}

			resolve(templates);
		});
	};

	private saveProduct = async () => {
		return new Promise<void>((resolve, reject) => {
			this.zkProduct &&
				this.zkProduct.save(null, {
					success: function (model: any, response: any, options: any) {
						resolve();
					},
					error: function (model: any, response: any, options: any) {
						reject(response);
					}
				});
		});
	};

	// Get single template
	private getTemplate = async (templateId: number) => {
		return new Promise<MPlaza.Design>((resolve, reject) => {
			let design = new MPlaza.Design();
			design.set('docID', templateId);
			design.fetch({
				success: async () => {
					resolve(design);
				},
				error: () => null,
				data: {
					template: true
				}
			});
			design.addEventListener('change', this.updatePrice);
		});
	};

	// Public methods
	public updateView = () => {
		this.viewer.updateView();
	};

	public selectOption = (optionId: number) => {
		const clone = this.cloneSelection(this.selection);
		this.undoStack.push(clone);

		let option: any;
		let attribute: any;

		this.zkProduct!.get('attributes').each((attr) => {
			let _option = attr.get('options').find((option) => option.get('optionID') == optionId);
			if (_option) {
				option = _option;
				attribute = attr;
			}
		});

		if (option) {
			// Don' t reselect it.
			if (option.selected) return;

			this.selection.selectAttributeValue(attribute.get('attributeID'), optionId.toString());
		} else {
			throw new Exception('Invalid option with id ' + optionId);
		}
	};

	public async selectOptionsbyPrompt(prompt: string) {
		if (!prompt) throw new Error('Invalid prompt');

		const response = (
			await axiosApi.get<[string, string][]>(Zakeke.config.baseApiUrl + 'ai/configuration-by-prompt', {
				params: { productId: this.product?.id }
			})
		).data;

		for (const [attributeName, optionName] of response) {
			const optionId = this.groups
				.flatMap((x) => x.attributes)
				.find((x) => x.name === attributeName)
				?.options.find((x) => x.name === optionName);

			if (optionId) {
				this.selectOption(optionId.id);
			}
		}
	}

	// Focus a group executing some camera movements
	public focusGroup = (groupId: number) => {
		const sceneGroup = this.groups.find((x) => x.id == groupId);

		if (sceneGroup && sceneGroup.cameraLocationId) {
			this.viewer.setCameraLocation(sceneGroup.cameraLocationId);

			this.lastCameraLocation = sceneGroup.cameraLocationId;
		}
	};

	public focusAttribute = async (attributeId: number) => {
		const unselectedOptionsActions = this.selection.getAttributeUnselectedOptionsSceneActions(attributeId);
		await this.viewer.prefetchDataForUnselectedOptionsAsync(unselectedOptionsActions);

		const attribute = this.zkProduct!.get('attributes').find((attribute) => attribute.get('attributeID') == attributeId);

		if (attribute && attribute.get('sceneCameraLocationID') && attribute.get('sceneCameraLocationID') != Zakeke.getEmptyUUID()) {
			this.viewer.setCameraLocation(attribute.get('sceneCameraLocationID')!);
			this.lastCameraLocation = attribute.get('sceneCameraLocationID');
		}
	};

	public setCamera(cameraLocationId: string, onlyAngleOfView?: boolean, animate?: boolean) {
		this.viewer.setCameraLocation(cameraLocationId, onlyAngleOfView, animate);
		this.lastCameraLocation = cameraLocationId;
	}

	public getCameraByName(name: string) {
		const location = this.zkScene?.get('cameraLocations').find((x) => x.get('name') == name);
		return location;
	}

	public setCameraLocation(name: string, animate = true) {
		const location = this.getCameraByName(name);

		if (!location) throw new Error(`Camera with name ${name} does not exists.`);

		this.viewer.setCameraLocation(location.get('locationID'), false, animate);
		this.lastCameraLocation = location.get('locationID');
	}

	private base64StringToFile = (base64String: string, filename: string) => {
		var arr = base64String.split(','),
			mime = arr[0]?.match(/:(.*?);/)?.[1] ?? '',
			bstr = atob(arr[1]),
			n = bstr.length,
			u8arr = new Uint8Array(n);

		while (n--) {
			u8arr[n] = bstr.charCodeAt(n);
		}

		return new File([u8arr], filename, { type: mime });
	};

	public saveTemplate = async (templateName: string, selectedCategoryID: number | null, attributeOptions: Map<number, number>, save2dDesign?: boolean) => {
		const attributes = Array.from(attributeOptions.keys());
		let compositionItems: MPlaza.CompositionItems = this.composition.get('compositionItems');
		compositionItems.toArray().forEach((compositionItem: MPlaza.CompositionItem) => {
			if (!attributes.includes(compositionItem.get('attributeID'))) compositionItems.remove(compositionItem);
		});
		this.composition.set('name', templateName);
		this.composition.set('compositionItems', compositionItems);
		this.composition.set('isTemplate', true);
		const categories = new MPlaza.CompositionCategoriesMapping();
		if (selectedCategoryID) {
			const mapping = new MPlaza.CompositionCategoryMapping();
			mapping.set('categoryID', selectedCategoryID);
			mapping.set('name', ' ');
			categories.add(mapping);
		} else if (this.selectedTemplateCategory) {
			try {
				const mapping = new MPlaza.CompositionCategoryMapping();
				const parsedCategory = this.selectedTemplateCategory.split(';');
				mapping.set('categoryID', Number.parseInt(parsedCategory[0]));
				mapping.set('name', parsedCategory[1]);
				categories.add(mapping);
			} catch (ex) {
				console.error('Error while parsing associated category for template', ex, this.selectedTemplateCategory);
			}
		}
		this.composition.set('categories', categories);

		if (this.zkProduct!.get('templateGroups').length === 0) {
			const templateGroup = new MPlaza.TemplateGroup();
			templateGroup.set('name', 'Pre-set configurations');

			if (this.zkProduct!.get('attributeGroups').length > 0) {
				const group = new MPlaza.AttributeGroup();
				group.set('name', 'Pre-set configurations');
				group.set('displayOrder', this.zkProduct!.get('attributeGroups').at(0).get('displayOrder') - 1);
				this.zkProduct!.get('attributeGroups').unshift(group);
				templateGroup.set('groupGuid', group.get('groupGuid'));
			} else {
				if (this.zkProduct!.get('attributeSteps') && this.zkProduct!.get('attributeSteps').length > 0) {
					const step = new MPlaza.AttributeStep();
					step.set('name', 'Pre-set configurations');
					step.set('groupGuid', Zakeke.generateUUID());
					step.set('displayOrder', this.zkProduct!.get('attributeSteps').at(0).get('displayOrder') - 1);
					this.zkProduct!.get('attributeSteps').unshift(step);
					templateGroup.set('stepGuid', step.get('stepGuid'));
				} else {
					const attributesWithoutGroup = this.zkProduct!.get('attributes')
						.filter((attr) => attr.get('groupID') === -1)
						.sort(ascComparator((x) => x.get('displayOrder')));

					if (attributesWithoutGroup.length > 0) templateGroup.set('displayOrder', attributesWithoutGroup[0].get('displayOrder') - 1);
				}
			}

			this.zkProduct!.get('templateGroups').add(templateGroup);
			await this.saveProduct();
		}

		await this.addToCart([], undefined, undefined, save2dDesign);
		await this.reset();
	};

	public addToCart = async (
		additionalProperties: Record<string, any>,
		onBeforeSendDataToParent?: (data: OnBeforeSendDataToParent) => Promise<Record<string, any>>,
		legacyScreenshot?: boolean,
		saveDesignFromTemplateEditor?: boolean
	) => {
		await this.setExplodedMode(false);
		if (this.pdSettings) this.composition.set('pdSettings', this.pdSettings);
		this.composition.set('compositionClientPreviews', []);
		const compositionClientPreviews: CompositionClientPreview[] = [];
		let sortedBuyScreenshotCameras: Zakeke.SceneCameraLocation[] = [];
		let previewFiles: PreviewFile[] = [];
		const buyScreenshotCameras = this.zkScene?.get('cameraLocations').filter((x) => x.get('name')?.toLowerCase().indexOf('buy_screenshot_camera_') !== -1);

		if (buyScreenshotCameras)
			sortedBuyScreenshotCameras = sortBy(buyScreenshotCameras, (a) => {
				return a.get('name');
			});

		if (sortedBuyScreenshotCameras && sortedBuyScreenshotCameras.length > 0) {
			for (const camera of sortedBuyScreenshotCameras) {
				const preview = await this.viewer.getSceneContentScreenshotAsync(
					this.clientPreviewWidth,
					this.clientPreviewHeight,
					0,
					this.zkCurrentTemplate,
					legacyScreenshot,
					camera,
					this.zkScene?.getGeneralConfig()?.get('screenshotBackgroundColor') ?? null
				);

				const formData = new FormData();
				if (!preview) return;
				const file = this.base64StringToFile(preview, camera + '.png');
				formData.set('file', file);

				// qui in caso di template dovrei prima rigenerare la preview, poi far salvare la composition
				const previewCameraFile = (await axiosApi.post<PreviewCameraImage>(Zakeke.config.baseApiUrl + 'compositions/upload-client-preview', formData))
					.data;
				compositionClientPreviews.push({
					fileObjectID: previewCameraFile.id,
					cameraName: camera!.get('name')!,
					url: previewCameraFile.url
				});

				let previewFile: PreviewFile = {
					url: previewCameraFile.url,
					cameraName: camera!.get('name')!
				};
				previewFiles.push(previewFile);

				await sleep(1);
			}
			this.composition.set('compositionClientPreviews', compositionClientPreviews);
		}
		// se entri in questo if significa che sei lato frontend ed hai applicato una composition draft,
		// quindi devi rimuovere i campi docID e designDocID dalla composition, altrimenti sovrascrivi il template
		// stesso discorso vale per il template editor, dove non vuoi che venga sovrascritto il template base

		if (this.composition.get('isTemplate') && saveDesignFromTemplateEditor) {
			this.composition.set('designDocID', null);
			this.composition.set('isDraft', false);

			if (this.zkCurrentTemplate) this.zkCurrentTemplate.set('docID', null);
		}
		if (this.composition.get('isDraft') && !this.isDraftEditor) {
			this.composition.set('docID', null);
			this.composition.set('designDocID', null);
			this.composition.set('isDraft', false);

			if (this.zkCurrentTemplate) this.zkCurrentTemplate.set('docID', null);
		}
		console.log('previewFiles', previewFiles);

		if (this.composition.get('isTemplate') && !saveDesignFromTemplateEditor && this.zkCurrentTemplate) {
			console.log('reset design from template editor');
			// qui non resetto il design perchè se il flusso è rispettato correttamente, per una composition template
			// se non devi salvare il design lo ignora il design attuale, quindi resetto il design solo per la
			// questione della preview
			this.viewer.resetDesign();
		}
		// Save composition

		// devo passare qui il flag sui design che viene dal template editor
		await this.saveComposition(undefined, legacyScreenshot, undefined, saveDesignFromTemplateEditor);

		// if (selectedCategory) {
		// 	const categoryParams = {
		// 		categoryId: selectedCategory!.categoryId,
		// 		macroCategoryId: selectedCategory!.macrocategoryId,
		// 		compositionId: this.composition.get('compositionID'),
		// 	};
		// 	await axiosApi.post<{ categoryId: number; macroCategoryId: number; compositionId: number }>(
		// 		Zakeke.config.baseApiUrl + 'compositionmacrocategories/assigntocategory',
		// 		categoryParams,
		// 	);
		// }

		if (onBeforeSendDataToParent) {
			additionalProperties = await onBeforeSendDataToParent({
				composition: this.composition.id,
				preview: this.composition.get('previewImageUrl'),
				attributes: this.platformAttributesSelection,
				designItems: this.getDesignItemsInfo(),
				quantity: this.quantity,
				previewFiles: previewFiles,
				additionalProperties
			});
		}

		// Add to cart
		this.integration.addToCart(
			this.composition,
			this.zkCurrentTemplate,
			this.zkProduct,
			this.platformAttributesSelection,
			this.getDesignItemsInfo(),
			this.quantity,
			additionalProperties
		);
	};

	public zoomIn = () => {
		if (this.viewer) this.viewer.zoomIn();
	};

	public zoomOut = () => {
		if (this.viewer) this.viewer.zoomOut();
	};

	public isFullscreenEnabled = () => {
		return !iOS;
	};

	public getDesignItemsInfo() {
		let items: Item[] = [];

		if (this.viewer) {
			const rawItems = this.viewer.getDesignItemsInfo(this.visibleCustomizer);
			items =
				rawItems?.map((item) => {
					return item instanceof MPlaza.ImageItemInfo ? new ImageItem(item) : new TextItem(item);
				}) ?? [];
		}

		return items;
	}

	public switchFullscreen = () => {
		let interval: number | null = null;
		this.viewer.switchFullScreen(document.querySelector('.zakeke-viewer-container'));

		if (!interval) {
			//eslint-disable-next-line
			interval = setInterval(() => {
				if (this.isFullscreenMode && this.internalIsFullscreen == false) {
					setTimeout(() => {
						this.viewer.updateView();
						// doppio resize per mobile
						this.viewer.updateView();
						this.internalIsFullscreen = true;

						console.info('Fullscreen enabled');
					}, 10);
				}

				if (!this.isFullscreenMode && this.internalIsFullscreen == true) {
					clearInterval(interval!);
					interval = null;

					setTimeout(async () => {
						this.internalIsFullscreen = false;

						// Update layout for two seconds
						await this.extendedUpdate();
						this.viewer.updateView();
						// doppio resize per mobile
						this.viewer.updateView();

						console.info('Fullscreen disabled');
					}, 10);
				}
			}, 100) as unknown as number;
		}
	};

	public openSecondScreen = () => {
		const url = Zakeke.config.baseUrl + 'en/SecondScreen?token=' + this.token + '&modelID=' + this.zkProduct!.get('modelID');
		window.open(url);
	};

	private extendedUpdate = async () => {
		return new Promise<void>((resolve, reject) => {
			let count = 0;
			let interval = setInterval(() => {
				this.viewer.updateView();

				if (++count > 20) {
					// After two seconds
					clearInterval(interval);
					resolve();
				}
			}, 100);
		});
	};

	public exportSceneToUsdz = async (hash: string) => {
		if (this.viewer && this.viewer.zkScene) return this.viewer.exportSceneToUsdzAsync(this.viewer.zkScene.get('sceneID'), hash);
	};

	public exportSceneToGlb = async () => {
		return this.viewer.exportSceneToLocalGLBAsync();
	};

	public setExplodedMode = async (explodedMode: boolean) => {
		if (this.viewer) {
			if (explodedMode) await this.viewer.explodeMeshes();
			else await this.viewer.collapseMeshes();
		}

		this.isExplodedMode = explodedMode;
	};

	public hasExplodedMode = () => {
		let zkScene = this.zkScene;

		if (zkScene) return !!zkScene.getFirstExplodedMeshLocation();
		else return false;
	};

	public setBackgroundColor = (color: string, alpha: number) => {
		let zkScene = this.zkScene;

		if (color.indexOf('rgb') == 0) {
			let values = color.replace('rgb(', '').replace(')', '').split(',');
			color = rgb2hex(values[0], values[1], values[2]);
		}

		if (this.viewer && zkScene) {
			zkScene.getOrCreateEnvironmentConfig()!.set('mainColor', color);
			zkScene.getOrCreateGeneralConfig()!.set('backgroundColor', color);
			zkScene.getOrCreateGeneralConfig()!.set('backgroundAlpha', alpha);
			this.viewer.refreshEnvironment();
		}
	};

	public getCurrentSelection = () => {
		return _.cloneDeep(this.platformAttributesSelection);
	};

	public getScreenshot = async (
		width = 1024,
		height = 1024,
		legacyScreenshot?: boolean,
		backgroundColor: string | null = null,
		padding: number = 0,
		camera: Zakeke.SceneCameraLocation | null = null
	) => {
		let url = await this.viewer.getSceneContentScreenshotAsync(
			width,
			height,
			padding,
			this.zkCurrentTemplate,
			legacyScreenshot,
			camera,
			this.zkScene?.getGeneralConfig()?.get('screenshotBackgroundColor') ?? null
		);

		if (!url) throw new Error('Failed generating screenshot');

		if (backgroundColor) {
			await new Promise<void>((resolve) => {
				const img = new Image();
				img.onload = () => {
					const canvas = document.createElement('canvas');
					canvas.width = img.width;
					canvas.height = img.height;
					const ctx = canvas.getContext('2d');

					if (ctx) {
						ctx.fillStyle = backgroundColor;
						ctx.fillRect(0, 0, img.width, img.height);
						ctx.drawImage(img, 0, 0);
						url = canvas.toDataURL();

						resolve();
					}
				};
				img.src = url!;
			});
		}
		return url;
	};

	private fireItemsChange = () => {
		this.trigger('change');
	};

	public isAreaVisible(areaId: number) {
		// Get the mesh side
		// in this case areaId is sideId
		const meshSides = this.zkProduct!.get('scenes')
			.at(0)
			.get('meshSides')
			.filter((x) => x.get('sideID') == areaId);

		for (const meshSide of meshSides) {
			const meshId = meshSide.get('meshID');

			if (meshId) {
				const mesh = this.viewer.scene!.getMeshByID(meshId);

				if (mesh && mesh.isVisible) return true;
			}
		}

		return false;
	}

	public removeItem = async (guid: string) => {
		return new Promise<void>((resolve, reject) => {
			if (this.viewer) {
				this.viewer.removeDesignItem(guid, this.visibleCustomizer);
				this.debouncedRefresh();
				this.fireItemsChange();
				resolve();
			} else {
				reject('Called remove item but no viewer or viewer not ready');
			}
		});
	};

	public async addItemText(settings: { text: string; fontFamily: string }, areaId: number) {
		if (this.viewer) {
			// First download the font
			const font = this.fonts.find((x) => x.name === settings.fontFamily);
			if (font) {
				await loadFonts([font]).catch((e) => {
					console.error('Error loading font', e, font);
				});

				this.viewer.addDesignItemText({ ...settings, content: settings.text }, areaId, this.visibleCustomizer);
				this.debouncedRefresh();
			} else {
				console.warn(`No font found for ${settings.fontFamily}`);
			}
		} else throw 'Called add item text but no viewer or viewer not ready';
	}

	public async setPrintingMethod(printingMethodId: number, areaId: number) {
		if (this.viewer) {
			this.viewer.setDesignPrintingMethod(printingMethodId, areaId, this.visibleCustomizer);
			this.debouncedRefresh();
		} else throw 'Called set printing method but no viewer or viewer not ready';
	}

	public getPrintingMethods(): Array<{ printMethodId: number; name: string }> {
		if (this.zkProduct) {
			const printTypes = this.zkProduct.get('printTypes');
			return printTypes.map((printType: any) => ({
				printMethodId: printType.get('printTypeID'),
				name: printType.get('name')
			}));
		}
		return [];
	}

	public moveItemUp = (guid: string) => {
		if (this.viewer) {
			this.viewer.moveDesignItemUp(guid, this.visibleCustomizer);
			this.debouncedRefresh();
			this.fireItemsChange();
		} else throw 'Called move item up but no viewer or viewer not ready';
	};

	public moveItemDown = (guid: string) => {
		if (this.viewer) {
			this.viewer.moveDesignItemDown(guid, this.visibleCustomizer);
			this.debouncedRefresh();
			this.fireItemsChange();
		} else throw 'Called move item down but no viewer or viewer not ready';
	};

	public setItemText = (guid: string, text: string) => {
		let item = this.getDesignItemsInfo().filter((x) => x.guid === guid)[0];
		let sanitizedText = sanitizeTextFromUnsupportedAndEmojiCharacters(item as TextItem, text, this.fonts);
		if (this.viewer) {
			const viewerText = sanitizedText || '⠀';
			this.viewer.setDesignItemText(guid, viewerText, this.visibleCustomizer);
			this.updateDesignFast(guid);
			this.fireItemsChange();
		} else throw 'Called set item text but no viewer or viewer not ready';
		return sanitizedText;
	};

	public setItemItalic = (guid: string, italic: boolean, font?: FontFamily) => {
		if (this.viewer) {
			// Get font if not provided
			if (!font && this.visibleCustomizer) {
				let item = this.visibleCustomizer.findItemByGuid(guid);
				if (item) {
					let info = this.visibleCustomizer.getTextItemInfo(item);
					font = this.fonts.find((f) => f.name === info.fontFamily);
				}
			}

			if (font) {
				this.updateFontWeightForTextItem(guid, font, undefined, italic);
				this.debouncedRefresh();
			}
		} else throw 'Called set item italic but no viewer or viewer not ready';
	};

	public setItemBold = (guid: string, bold: boolean, font?: FontFamily) => {
		if (this.viewer) {
			// Get font if not provided
			if (!font && this.visibleCustomizer) {
				let item = this.visibleCustomizer.findItemByGuid(guid);
				if (item) {
					let info = this.visibleCustomizer.getTextItemInfo(item);
					font = this.fonts.find((f) => f.name === info.fontFamily);
				}
			}

			if (font) {
				this.updateFontWeightForTextItem(guid, font, bold, undefined);
				this.debouncedRefresh();
			}
		} else throw 'Called set item bold but no viewer or viewer not ready';
	};

	public setItemColor = (guid: string, color: string) => {
		if (this.viewer) {
			this.viewer.setDesignItemColor(guid, color, this.visibleCustomizer);
			this.debouncedRefresh();
		} else throw 'Called set item color but no viewer or viewer not ready';
	};

	public setItemFontFamily = async (guid: string, fontFamily: string) => {
		if (this.viewer) {
			const font = this.fonts.find((x) => x.name === fontFamily);

			if (font) {
				// IMPORTANT: Read current styles BEFORE changing the font
				const item = this.getDesignItemsInfo().find((di) => di.guid === guid) as TextItem;
				if (!item) {
					console.warn('[setItemFontFamily] Item not found:', guid);
					return;
				}

				const itemWeight = item.fontWeight.split(' ');
				const wasItalic = itemWeight.length > 1 && itemWeight[0] === 'italic';
				const wasBold = itemWeight.length > 1 ? parseInt(itemWeight[1]) > 500 : parseInt(itemWeight[0]) > 500;

				// Load the new font
				await loadFonts([font]).catch((e) => {
					console.error('Error loading font', e, font);
				});

				// Change the font family
				this.viewer.setDesignItemFontFamily(guid, fontFamily, this.visibleCustomizer);

				// Check new font capabilities
				const fontWeightsArray = this.viewer.getFontWeights(font);
				const hasBold = fontWeightsArray.some((w) => w.style === 'normal' && w.weight > 500);
				const hasItalic = fontWeightsArray.some((w) => w.style === 'italic');

				// Re-apply or deactivate based on availability
				if (wasBold) {
					if (hasBold) {
						this.viewer.setDesignItemBold(guid, true, font, this.visibleCustomizer);
					} else {
						this.viewer.setDesignItemBold(guid, false, font, this.visibleCustomizer);
					}
				}

				if (wasItalic) {
					if (hasItalic) {
						this.viewer.setDesignItemItalic(guid, true, font, this.visibleCustomizer);
					} else {
						this.viewer.setDesignItemItalic(guid, false, font, this.visibleCustomizer);
					}
				}

				this.debouncedRefresh();
				this.fireItemsChange();
			}
		} else throw 'Called set item font family but no viewer or viewer not ready';
	};
	public setItemTextOnPath = (guid: string, areaId: number, value: boolean) => {
		if (this.viewer) {
			this.viewer.setItemTextOnPath(guid, areaId, value, this.visibleCustomizer);
			this.debouncedRefresh();
			this.fireItemsChange();
		} else throw 'Called toggleItemTextOnPath but no viewer or viewer not ready';
	};

	public async fullyLoadFont(font: string | FontFamily) {
		if (typeof font === 'string') {
			const fontFamily = this.fonts.find((x) => x.name === font);
			if (fontFamily) {
				if (!fontFamily.fullLoaded) await loadFonts([fontFamily]);
				return fontFamily;
			} else {
				return this.fonts[0];
			}
		} else {
			if (!font.fullLoaded) await loadFonts([font]);
			return font;
		}
	}

	public sanitizeString = (family: FontFamily, text: string) => {
		const face = family.faces[0] ?? null;
		if (!face) return text;
		return sanitizeString(face, text);
	};

	public getSanitationText = (family: FontFamily, text: string): TextSanitationResult => {
		const face = family.faces[0] ?? null;
		if (!face) return { dirtyChars: [], sanitizedText: text };
		return sanitizeText(face, text);
	};

	public createImage = async (file: File, progress?: (percentage: number) => void): Promise<ZakekeImage> => {
		if (this.viewer) {
			const imageTask = new ImageUploadTask(this.culture, this.token);
			const printingMethods = this.zkProduct?.get('printTypes');
			imageTask.addPrintingOptions(printingMethods);
			const result = await imageTask.upload(file, progress);
			const image = result ? result[0] : null;
			return image;
		} else throw 'Called create image but no viewer or viewer not ready';
	};

	public createImageFromUrl = async (url: string): Promise<ZakekeImage> => {
		if (this.viewer) {
			const result = (
				await axiosApi.post<ZakekeImage>(Zakeke.config.baseApiUrl + 'images/import/url', {
					url
				})
			).data;
			return result;
		} else throw 'Called create image from url but no viewer or viewer not ready';
	};

	public addItemImage = async (id: number, areaId: number) => {
		if (this.viewer) {
			const guid = await this.viewer.addDesignItemImage(id, areaId, this.visibleCustomizer);
			this.debouncedRefresh();
			this.fireItemsChange();

			return guid;
		} else throw 'Called add item text but no viewer or viewer not ready';
	};

	public setItemImageFromFile = async (guid: string, file: File, progress?: (percentage: number) => void) => {
		if (this.viewer) {
			const image = await this.createImage(file, progress);
			await this.setItemImage(guid, image.imageID);
			this.fireItemsChange();
		} else throw 'Called set item image but no viewer or viewer not ready';
	};

	// For preview only!!
	public setItemImageFromBase64 = async (guid: string, base64: string) => {
		if (this.viewer) {
			const image = new MPlaza.Image();
			image.set('imageID', -1);
			image.set('previewUrl', base64);
			await this.viewer.setDesignItemImage(guid, image, this.visibleCustomizer);
			this.debouncedRefresh();
			this.fireItemsChange();
		} else throw 'Called set item image but no viewer or viewer not ready';
	};

	public setItemImage = async (guid: string, id: number) => {
		return new Promise<void>(async (resolve, reject) => {
			if (this.viewer) {
				const image = new MPlaza.Image();
				image.set('imageID', id);
				image.fetch({
					success: async (model, response, opts) => {
						await this.viewer.setDesignItemImage(guid, image, this.visibleCustomizer);
						this.debouncedRefresh();
						this.fireItemsChange();
						resolve();
					},
					error: (model, response, opts) => null
				});
			} else {
				throw 'Called set item image but no viewer or viewer not ready';
			}
		});
	};

	public setItemSize = async (guid: string, width: number, height: number) => {
		if (this.viewer) {
			await this.viewer.setDesignItemSize(guid, width, height, this.visibleCustomizer);
			console.log('finished item size');
			this.debouncedRefresh();
			this.fireItemsChange();
		}
	};

	public setItemConstraints = async (guid: string, constraints: { canMove?: boolean; canResize?: boolean; canRotate?: boolean; canDelete?: boolean }) => {
		if (this.viewer) {
			this.viewer.setDesignItemConstraints(guid, constraints, this.visibleCustomizer);
			this.debouncedRefresh();
			this.fireItemsChange();
		}
	};

	public T_static = (str: string) => {
		if (this.internalTranslations?.statics) {
			for (let translation of this.internalTranslations.statics) {
				if (translation.original.toLowerCase() === str.toLowerCase()) {
					return translation.localization;
				}
			}
		}

		let gt = (window as any).gt;
		return gt ? gt.dgettext('Composer', str) : str;
	};

	// Metodo pubblico per traduzioni dinamiche
	public T_dynamic = (str: string) => {
		str = str.trim();
		if (this.internalTranslations?.dynamics) {
			for (let translation of this.internalTranslations.dynamics) {
				if (translation.original.toLowerCase() === str.toLowerCase()) {
					return translation.localization || str;
				}
			}
		}
		return str;
	};

	public getMacroCategories = async () => {
		let macroCategories = (
			await axiosApi.get<ImageMacroCategory[]>('imagemacrocategories/', {
				params: {
					productID: this.product?.id
				}
			})
		).data;
		macroCategories = macroCategories.filter((x) => x.hasImages);

		for (let macroCategory of macroCategories) macroCategory.categories = macroCategory.categories.filter((x) => x.hasImages);
		return macroCategories;
	};

	public getImages = async (categoryId: number) => {
		let images = (
			await axiosApi.get<ZakekeImage[]>('images', {
				params: {
					type: 'lib',
					c: categoryId,
					productID: this.product?.id
				}
			})
		).data;
		return images;
	};

	public setCameraPivot(meshID: string) {
		this.viewer.setCameraPivot(meshID);
	}

	public resetCameraPivot() {
		this.viewer.setCameraPivot(null);
	}

	public setCameraZoomEnabled(enabled: boolean) {
		this.viewer.setCameraZoomEnabled(enabled);
	}

	public setCameraByName(name: string, onlyAngleOfView?: boolean, animate?: boolean) {
		if (this.zkScene) {
			const location = this.zkScene.get('cameraLocations').find((x) => x.get('name') == name);

			if (location) this.viewer.setCameraLocation(location.get('locationID'), onlyAngleOfView, animate);
		}
	}

	public findObjectByName(name: string) {
		if (this.viewer && this.viewer.scene) return this.viewer.scene.meshes.find((x) => x.name == name);
		else return null;
	}

	public getPDF = async (): Promise<string> => {
		const pdfScreenshotCamera = this.zkScene?.get('cameraLocations').find((x) => x.get('name')?.toLowerCase() === 'pdf_screenshot_camera');
		const data = await this.getScreenshot(2048, 2048, false, null, 0, pdfScreenshotCamera);

		const items = this.getDesignItemsInfo();

		const areas = this.product?.areas
			.filter((x) => this.isAreaVisible(x.id))
			.map((x) => ({
				name: x.name,
				textItems: items.filter((i) => i.areaId == x.id && i.type === ItemType.Text)
			}))
			.filter((x) => x.textItems.length > 0);

		const selectedOptions = this.platformAttributesSelection
			.filter((x) => x.attributeIsEnabled && x.attributeIsEnabled)
			.map(({ attributeLabel, attributeCode, optionCode, optionLabel }) => ({
				attributeLabel,
				attributeCode: !isPlatformCode(attributeCode) ? attributeCode : '',
				optionLabel,
				optionCode: !isPlatformCode(optionCode) ? optionCode : ''
			}));

		const params = {
			environment: 2,
			productName: this.zkProduct!.get('name'),
			previewImage: data,
			sides: [],
			areas,
			selectedOptions
		};

		const pdf = (
			await axiosApi.post(Zakeke.config.baseApiUrl + 'previewdesigns/pdf', params, {
				transformResponse: (res) => res,
				responseType: 'text'
			})
		).data.replace(/"/g, '');

		return pdf;
	};

	private rewriteShareUrl = (url: string) => {
		const path = url.split('/SharedPreview')[1];

		if (this.shareUrlPrefix) {
			return this.shareUrlPrefix + path;
		}

		if (!this.isShoppingPreview && document.referrer && this.settings.referrerID === 2) {
			const regex = /(apps\/configurator\/)(.*)/gms;
			let firstPart = document.referrer.replace(regex, '$1');

			if (!firstPart.endsWith('/')) firstPart += '/';

			return firstPart + path;
		}

		return url;
	};

	public getShareCompositionUrl = async () => {
		// salvo (e riassegno dopo la creazione dello share url) l'id , per evitare problemi in caso di add to cart e edit
		// in questo modo ogni composizione condivisa è sempre diversa da quella di partenza

		const isDraft = this.composition.get('isDraft') ?? false;
		this.composition.set('isDraft', false);

		const docID = await this.saveCompositionCopy(true);
		this.composition.set('isDraft', isDraft);

		const result = await this.integration.getSharedCompositionUrl(docID!);

		//link for local test
		//const result = "https://localhost:44363/scripts/composer/Themes/DefaultTheme/public/index.html?name=Test%20Elena%203D&modelCode=65&currency=EUR&ecommerce=preview&qty=1&culture=en-US&token=eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJyb2xlIjoiU2VsbGVyIiwiUGVybUJpdHMiOiIwMTAwMTExMDAxMTAwMTExMTExMTAxIiwidW5pcXVlX25hbWUiOiJuaWNvbGV0dG8iLCJVc2VySUQiOiIyNCIsIlVzZXJOYW1lIjoibmljb2xldHRvIiwiZW1haWwiOiJuLmJpenpvY2FAZnV0dXJlbmV4dC5pdCIsIlVzZXJUeXBlSUQiOiIzIiwiSXNzdWVEYXRlIjoiMDgtMDctMjAyMiAwOC0xMi01OFoiLCJVc2VyVmVyc2lvbiI6IjIwMjIwNzA3MTczOTU4IiwiY2xpZW50SUQiOiIyNjM0Iiwic2FsZXNDaGFubmVsSUQiOiIxIiwiY3VzdG9tZXJjb2RlIjoidGVzdCBjdXN0b21lciIsImFjY2Vzc1R5cGUiOiJTMlMiLCJuYmYiOjE2NTcyNjc5NzgsImV4cCI6MTY1NzM1NDM3OCwiaWF0IjoxNjU3MjY3OTc4LCJpc3MiOiJ3d3cuemFrZWtlLmNvbSIsImF1ZCI6Imh0dHBzOi8vd3d3Lnpha2VrZS5jb20ifQ.jQynLW6sZi3mtk5f05DaebVKfcwtbSyPT_JMd1HDM88&fromUrl=1&viewerMode=1&sharedCompositionDocId="+ docID;

		return result.url;
	};

	public saveCompositionCopy = async (isSharing?: boolean) => {
		let docId: string | null | undefined = null;
		let designDocId: string | null | undefined = null;

		let currentTemplate = this.zkCurrentTemplate;

		if (currentTemplate) {
			designDocId = currentTemplate.get('docID');
			currentTemplate.set('docID', null);
		}

		docId = this.composition.get('docID');

		this.composition.set('docID', null);
		this.composition.set('designDocID', null);

		await this.saveComposition(undefined, undefined, isSharing);

		const newCompositionDocId = this.composition.get('docID');

		this.composition.set('docID', docId);
		this.composition.set('designDocID', designDocId);
		if (currentTemplate) currentTemplate.set('docID', designDocId);

		return newCompositionDocId;
	};

	public getOnlineScreenshot = async (
		width: number,
		height: number,
		legacyScreenshot?: boolean,
		backgroundColor?: string,
		padding: number = 30
	): Promise<{ originalUrl: string; rewrittenUrl: string }> => {
		const preview = await this.getScreenshot(width, height, legacyScreenshot, backgroundColor, padding);

		const { url: originalUrl } = (await axiosApi.post(Zakeke.config.baseApiUrl + 'designs/save-share-preview', { preview })).data;
		const rewrittenUrl = this.rewriteShareUrl(originalUrl);

		return {
			originalUrl,
			rewrittenUrl
		};
	};

	// AR - Augmented Reality

	public isSceneArOnTheFly = () => {
		if (this.viewer) return this.zkScene?.getAROnTheFly();
		return false;
	};

	public isArDeviceCompliant = () => IS_AR_DEVICE_COMPLIANT;
	public isAndroidOrIOS = () => IS_ANDROID || IS_IOS;

	public isAndroid = IS_ANDROID;
	public isIOS = IS_IOS;

	private buildModelViewerUrl = (fileInfos: { format: string; arFileUrl: string }[]) => {
		const url = Zakeke.config.modelViewerPage;
		const link = new URL(url);
		let search = '';

		for (const file of fileInfos) {
			if (file.format && file.arFileUrl) {
				const encodedUrl = encodeURIComponent(file.arFileUrl);
				let searchFile = '';

				switch (file.format) {
					case 'gltf':
					case 'glb':
						searchFile = 'gltf=' + encodedUrl;
						break;
					case 'usdz':
						searchFile = 'usdz=' + encodedUrl;
						break;
					case 'png':
						searchFile = 'poster=' + encodedUrl;
						break;
				}

				if (searchFile) search += (search ? '&' : '') + searchFile;
			}
		}

		search += '&arScale=' + (this.zkScene!.getARScale() ? 1 : 0);
		search += '&arAnchor=' + (this.zkScene!.getARAnchor() ?? '');
		link.search += search;

		const pageUrl = link.toString();
		return pageUrl;
	};

	public isSceneArEnabled = () => {
		if (this.zkScene) return this.zkScene.getAREnabled() || false;

		return false;
	};

	private getPregeneratedARUrl = (fileType: 'glb' | 'usdz') => {
		return this.zkScene
			?.get('arFiles')
			?.find((x) => x.get('fileType') == fileType)
			?.get('url');
	};

	public getQrCodeArUrl = async (device: 'iOS' | 'Android'): Promise<string> => {
		let fileInfos: { format: string; arFileUrl: string }[] = [];
		// HOTFIX for Coutie while RapidCompact resolves the issue with iOS AR
		const isCoutie = this.settings.userID === 271265;

		const sceneCode = this.isDAMViewer ? (this.zkProduct?.get('code') ?? undefined) : this.selection.getCompositionHashCode();

		if (device == 'Android' || (device == 'iOS' && isCoutie)) {
			const url = this.getPregeneratedARUrl('glb') || (await this.viewer.exportSceneToRemoteGLBAsync(this.zkScene!.get('sceneID'), sceneCode, false));

			if (url) fileInfos = [{ format: 'gltf', arFileUrl: url }];
		} else {
			const glb = this.getPregeneratedARUrl('glb');
			const usdz = this.getPregeneratedARUrl('usdz');

			if (glb && usdz)
				fileInfos = [
					{ format: 'glb', arFileUrl: glb },
					{ format: 'usdz', arFileUrl: usdz }
				];
			else fileInfos = await this.viewer.exportSceneToARAsync(this.zkScene!.get('sceneID'), sceneCode);
		}

		if (fileInfos.length == 0) return '';

		if (this.isDAMViewer) {
			this.makeDAMAnalyticsView(DAMViewerAnalyticsEventType.ViewArProdutc);
		}

		return this.buildModelViewerUrl(fileInfos);
	};

	public getMobileArUrl = async (): Promise<string | null> => {
		const sceneCode = this.isDAMViewer ? (this.zkProduct?.get('code') ?? undefined) : this.selection.getCompositionHashCode();
		let url: string | null = null;
		// HOTFIX for Coutie while RapidCompact resolves the issue with iOS AR
		const isCoutie = this.settings.userID === 271265;

		try {
			await document.requestStorageAccess();
		} catch {
			console.error('Storage access denied');
		}

		if (this.isSceneArOnTheFly()) {
			return (url = await this.getMobileLocalArUrl());
		} else {
			// added pregenerated flow for mobile too
			if (IS_IOS) {
				const pregeneratedUrl = this.getPregeneratedARUrl(isCoutie ? 'glb' : 'usdz');
				if (pregeneratedUrl) {
					url = pregeneratedUrl;
				} else {
					if (isCoutie) {
						console.log('Coutie user detected, exporting scene to remote GLB...');
						url = await this.viewer.exportSceneToRemoteGLBAsync(this.zkScene!.get('sceneID'), sceneCode, false);
					} else {
						url = await this.viewer.exportSceneToUsdzAsync(this.zkScene!.get('sceneID'), sceneCode, false);
					}
				}
			} else {
				url = this.getPregeneratedARUrl('glb') || (await this.viewer.exportSceneToRemoteGLBAsync(this.zkScene!.get('sceneID'), sceneCode, false));
			}

			// if (IS_IOS) {
			// 	url = await this.viewer.exportSceneToUsdzAsync(this.zkScene!.get('sceneID'), sceneCode, false);
			// } else {
			// 	url = await this.viewer.exportSceneToRemoteGLBAsync(this.zkScene!.get('sceneID'), sceneCode, false);
			// }
		}
		if (this.isDAMViewer) {
			this.makeDAMAnalyticsView(DAMViewerAnalyticsEventType.ViewArProdutc);
		}

		return url;
	};

	public getMobileLocalArUrl = () => {
		return new Promise<string>((resolve, reject) => {
			if (this.zkScene && this.viewer) {
				const fileId = this.zkScene.getSceneID();
				if (!fileId) {
					reject('Scene ID is missing.');
					return;
				}

				this.viewer
					.getSceneRootUrl()
					.then(async (rootUrl) => {
						if (!rootUrl) {
							reject('Root URL is missing.');
							return;
						}

						if (IS_IOS) {
							const url = await this.viewer.exportSceneToLocalUSDZAsync().catch((ex) => {
								console.error('Failed exporting scene to usdz', ex);
								return null;
							});

							if (!url) {
								reject('Failed to export scene to USDZ.');
							} else {
								resolve(url);
							}
						} else {
							this.viewer
								.saveGlbOnDatabaseAsync(fileId)
								.then((uploadedModelUrl: string) => {
									// Base AR file URL
									const arFileUrl = Zakeke.config.onFlyArPage;
									const link = new URL(arFileUrl);

									// Append query parameters
									let search = `?uploadedModelUrl=${uploadedModelUrl}`;
									search += `&rootUrl=${encodeURIComponent(rootUrl)}`;
									search += `&productId=${this.product!.id}`;
									search += `&environment=${this.product!.environment}`;
									search += `&token=${this.token}`;

									if (this.zkScene!.getARAnchor()) {
										search += `&arAnchor=${this.zkScene!.getARAnchor()}`;
									}

									link.search += search;
									const pageUrl = link.toString();

									// Resolve with the final URL
									resolve(pageUrl);
								})
								.catch((ex) => {
									console.error('Failed saving glb on database', ex);
									reject(ex);
								});
						}
					})
					.catch((ex) => {
						console.error('Failed to get scene root URL', ex);
						reject(ex);
					});
			} else {
				reject('Viewer or Scene is missing.');
			}
		});
	};

	public openArMobile = (url: string) => {
		// HOTFIX for Coutie while RapidCompact resolves the issue with iOS AR
		const isCoutie = this.settings.userID === 271265;

		if (this.viewer && this.zkScene) {
			if (this.isSceneArOnTheFly() && IS_ANDROID) {
				console.log('sono openArMobile e sono su android', url);
				window.open(url, '_blank');
			} else if (IS_IOS) {
				if (url && !this.zkScene.getARScale()) url += '#allowsContentScaling=0';

				if (isCoutie) {
					const fileInfos = [{ format: 'glb', arFileUrl: url }];

					const previewUrl = this.buildModelViewerUrl(fileInfos);
					window.open(previewUrl, '_blank');
					return;
				}

				const anchor = document.createElement('a');
				anchor.setAttribute('rel', 'ar');
				anchor.setAttribute('href', url);
				anchor.appendChild(document.createElement('img'));
				anchor.click();
			} else if (IS_ANDROID) {
				AndroidAR.openSceneViewer(
					url,
					() => {
						alert('Device not supported.');
					},
					this.zkScene.getARScale(),
					this.zkScene.getARAnchor()
				);
			}
		}
	};

	private registerView = async () => {
		if (!this.zkProduct || this.isShoppingPreview) return;

		try {
			await axiosApi.post(Zakeke.config.baseApiUrl + 'viewscount/addview', {
				productID: this.zkProduct.get('modelID'),
				environment: this.zkProduct.get('environment'),
				ecommerceID: this.settings.ecommerceID
			});
		} catch (ex) {
			console.error('Failed registering view', ex);
		}
	};

	// Manipulate scene
	public getMeshIDbyName = (name: string) => {
		return this.viewer?.getMeshByName(name)?.id;
	};

	public hightlightGroup(groupId: number) {
		this.viewer?.highlightGroupByID(groupId);
	}

	public highlightAttribute(attributeId: number) {
		this.viewer?.highlightAttributeByID(attributeId);
	}

	/**
	 * Hide a mesh and its children and save the state
	 * @param meshId The mesh and childs to hide
	 */
	public hideMeshAndSaveState = (meshId: string) => {
		if (this.viewer.scene) {
			const rootMesh = this.viewer.getMeshByID(meshId);

			// Add mesh and all submesh visibility to previousMeshVisibileStates
			if (rootMesh) {
				const meshes = [rootMesh, ...rootMesh.getChildMeshes()];

				for (const mesh of meshes) {
					this.previousMeshesVisibleStates[mesh.id] = mesh.isVisible;
					mesh.isVisible = false;
				}

				this.viewer.setRenderingNeeded();
			}
		}
	};

	/**
	 * Restore a mesh visibility state from a previous saved one (see hideMeshAndSaveState)
	 * @param meshId The mesh and childs to show
	 */
	public restoreMeshVisibility = (meshId: string) => {
		if (this.viewer.scene) {
			const rootMesh = this.viewer.getMeshByID(meshId);

			// Add mesh and all submesh visibility to previousMeshVisibileStates
			if (rootMesh && this.previousMeshesVisibleStates[rootMesh.id] !== undefined) {
				const meshes = [rootMesh, ...rootMesh.getChildMeshes()];

				for (const mesh of meshes) {
					mesh.isVisible = this.previousMeshesVisibleStates[mesh.id];
					delete this.previousMeshesVisibleStates[mesh.id];
				}

				this.viewer.setRenderingNeeded();
			}
		}
	};

	/**
	 * Change opacity for a specific mesh
	 * @param meshId The mesh to change the opacity
	 * @param opacity The opacity to set
	 */
	public setMeshDesignVisibility = (meshId: string, visible: boolean) => {
		if (this.viewer) {
			this.viewer.setDesignVisibility(meshId, visible);
			this.viewer.setRenderingNeeded();
		}
	};

	// Events
	public addFocusAttributesListener = (listenerFunction: FocusAttributesEventListener) => {
		if (this.viewer) this.viewer.addFocusAttributesListener(listenerFunction);
	};

	public clearListeners = () => {
		if (this.viewer) this.viewer.clearListeners();
	};

	public setCameraLocked = (isBlocked?: boolean) => {
		return this.viewer.setCameraLocked(isBlocked);
	};

	public isSceneTryOnEnabled = () => {
		if (this.viewer) if (this.zkScene) console.log(this.zkScene.getTryOnEnabled());
		if (this.viewer) if (this.zkScene) return this.zkScene.getTryOnEnabled() ?? false;

		return false;
	};

	public getTryOnUrl = (tryOnUrl?: string) => {
		return new Promise<string>((resolve) => {
			if (this.zkScene && this.viewer) {
				const fileId = this.zkScene.getSceneID();
				this.viewer.getSceneRootUrl().then((rootUrl) => {
					if (!rootUrl) return;

					const attachmentPoint = 'noseBridge';

					// Salvo le mesh su IndexedDB
					this.viewer.saveVisibleMeshesOnDatabaseAsync(fileId).then(() => {
						const url = tryOnUrl ?? 'https://zakekecdn.blob.core.windows.net/cdn/c/tryon/index.html';

						const link = new URL(url);
						let search = 'id=' + fileId;
						search += '&rootUrl=' + encodeURIComponent(rootUrl);
						search += '&attachmentPoint=' + attachmentPoint;

						search += '&productId=' + this.zkProduct!.get('modelID');
						search += '&environment=' + this.zkProduct!.get('environment');
						search += '&token=' + this.token;

						link.search += search;
						const pageUrl = link.toString();
						resolve(pageUrl);
					});
				});
			}
		});
	};

	public saveComposition = async (
		customPreviewSize?: CustomPreviewSize,
		legacyScreenshot?: boolean,
		isSharing?: boolean,
		saveDesignFromTemplateEditor?: boolean
	) => {
		console.log('Saving...');
		this.isCompositionSaving = true;
		this.trigger('change');
		try {
			const buyScreenshotCamera = this.zkScene?.get('cameraLocations').find((x) => x.get('name')?.toLowerCase() === 'buy_screenshot_camera');
			if (!buyScreenshotCamera) this.viewer.restoreCameraState();

			await sleep(1);
			console.log('data for screen', this.zkCurrentTemplate);
			// Save image
			const data = await this.viewer.getSceneContentScreenshotAsync(
				customPreviewSize?.customCartPreviewHeight ?? this.previewWidth,
				customPreviewSize?.customCartPreviewWidth ?? this.previewHeight,
				0,
				this.zkCurrentTemplate,
				legacyScreenshot,
				buyScreenshotCamera,
				this.zkScene?.getGeneralConfig()?.get('screenshotBackgroundColor') ?? null
			);
			if (this.pdSettings) this.composition.set('pdSettings', this.pdSettings);
			this.composition.set('previewImageData', data);
			// Save composition
			// salvo la composition se ha un design e , se siamo nel template editor, se ho scelto nella dialog di salvare anche il design
			// non salvo il design se siamo nel template editor e non ho scelto di salvare il design
			console.log('zkCurrentTemplate', this.zkCurrentTemplate);
			if (this.zkCurrentTemplate) {
				if (this.composition.get('isTemplate') && !saveDesignFromTemplateEditor) {
					console.log('Composition is a template, not saving design');
					this.composition.set('designDocID', null);
					// resetto il designID perchè su un template con design,
					// quando ho provato ad aggiornarlo rimuovendo il design,
					// ha recuperato lo stesso, quindi qui è da ripulire anche il designID
					this.composition.set('designID', -1);
				} else {
					let design = this.zkCurrentTemplate;
					let designItems = design.get('designItems');
					let itemsNotPlaceholders = designItems.filter((designItem) => !JSON.parse(designItem.get('json')!)[1].data.isTemplateElement);
					itemsNotPlaceholders.forEach((item) => {
						if (item.get('areas').length > 0 && !this.isAreaVisible(item.get('areas').at(0).get('sideID'))) designItems.remove(item);
					});
					const nonStaticElementsCount = designItems
						?.toArray()
						.map((designItem) => {
							return (
								!designItem!.get('constraints') ||
								designItem!.get('constraints')!.get('canResize') ||
								designItem!.get('constraints')!.get('canMove') ||
								designItem!.get('constraints')!.get('canDelete') ||
								designItem!.get('constraints')!.get('canRotate') ||
								designItem!.get('constraints')!.get('canEdit') ||
								designItem!.get('constraints')!.get('isPrintable')
								//  && designItem.get('areas').length > 0 &&
								// this.isAreaVisible(designItem.get('areas').at(0).get('sideID'))
							);
						})
						.filter((x) => x).length;

					if (nonStaticElementsCount > 0) {
						console.log('Saving design with non-static elements');
						await this.saveDesignBackbone(isSharing);
					} else this.composition.set('designDocID', null);
				}
			} else this.composition.set('designDocID', null);

			await this.saveCompositionBackbone(saveDesignFromTemplateEditor, isSharing);

			console.log('Composition saved!');
		} finally {
			this.isCompositionSaving = false;
			this.trigger('change');
		}
	};

	private bootstrapComposition = async (composition: MPlaza.Composition) => {
		this.selection = Zakeke.ComposerSelection.CreateComposerSelection(this.zkProduct!);
		this.composition = composition;
		await this.internalLoadComposition(this.composition.id);

		if (!this.currentCompositionInfo)
			this.currentCompositionInfo = {
				compositionId: '',
				compositionName: '',
				compositionTags: []
			};

		this.currentCompositionInfo.compositionId = this.compositionId;
		this.currentCompositionInfo.compositionName = this.composition.get('name');
		this.currentCompositionInfo.compositionTags = this.composition.get('tags');
	};

	public loadSavedComposition = async (docID: string) => {
		const composition = this.savedCompositions?.find((x) => x.docID == docID);
		let compositionModel = new MPlaza.Composition(composition);

		if (composition) await this.bootstrapComposition(new MPlaza.Composition(composition));
		this.selection.setupOnComposition(compositionModel);

		await this.sync3DAndPlatformSelectionWithCurrentSelection();

		this.buildStructure();
		this.updatePrice();

		this.selection.getAllAttributeSelections().forEach((attributeSelection) => {
			const selectionOption = attributeSelection.get('options').find((x) => x.get('selected'));

			// Change platform selection
			let platformSelection = this.platformAttributesSelection.find((x) => {
				return matchCodes(x.attributeCode, attributeSelection.get('attributeCode')!);
			});

			if (platformSelection && selectionOption) {
				platformSelection.optionCode = selectionOption.get('optionCode')!;
				platformSelection.optionLabel = this.T_dynamic(selectionOption.get('name')!);
			}
			console.log({ platformSelection });
		});
	};

	private updateSavedCompositions = async () => {
		const savedCompositions: Composition[] | null = (
			await axiosApi.get(Zakeke.config.baseApiUrl + `compositiondocs/product/${this.zkProduct!.get('modelID')}`, {
				params: {
					draft: true
				}
			})
		).data;

		this.savedCompositions = savedCompositions;

		if (savedCompositions) {
			const themeCompositions = this.themeCompositionsFromSavedCompositions(savedCompositions);
			this.themeCompositions = themeCompositions;
		}

		this.trigger('change');
	};

	public themeCompositionsFromSavedCompositions = (compositions: Composition[]) => {
		const themeCompositions = compositions.map((composition) => {
			return {
				name: composition.name,
				docID: composition.docID,
				previewImageUrl: composition.previewImageUrl,
				tags: composition.tags
			};
		});
		return themeCompositions;
	};

	public saveDraftsComposition = async (name: string, tags: string[], isCopy?: boolean) => {
		this.composition.set('name', name);
		this.composition.set('tags', tags);
		this.composition.set('isDraft', true);
		if (isCopy) await this.saveCompositionCopy();
		else await this.saveComposition(undefined);
		return this.updateSavedCompositions();
	};

	public deleteSavedComposition = async (docId: string) => {
		await axiosApi.delete(Zakeke.config.baseApiUrl + `compositiondocs/${docId}`);
		this.updateSavedCompositions();
	};

	public applyTemplate = async (templateGroupCompositionId: number) => {
		let result;
		try {
			result = await (
				await axiosApi.get<Composition>('proxycompositions/' + templateGroupCompositionId, {
					params: {
						template: true
					}
				})
			).data;
			let compositionItems: MPlaza.CompositionItems = this.composition.get('compositionItems');
			let compositionItemsFromTemplate = new MPlaza.CompositionItems(result.compositionItems);
			let newCompositionItems = new MPlaza.CompositionItems();

			// La configurazione del preset (template) va applicata PER INTERO: vanno inclusi tutti
			// gli item del template, anche quelli relativi ad attributi non presenti nella composizione
			// corrente (es. attributi abilitati da uno Style diverso da quello attuale). Il merge
			// precedente iterava solo sugli item correnti, perdendo così i valori del template per
			// quegli attributi (es. Color 1/Color 2 quando lo Style corrente era FLAT -> i colori WAVE
			// del preset venivano scartati e tornavano ai default).
			compositionItemsFromTemplate.toArray().forEach((templateCompositionItem: MPlaza.CompositionItem) => {
				newCompositionItems.add(templateCompositionItem);
			});

			// Mantengo gli item correnti solo per gli attributi che il template non specifica.
			compositionItems.toArray().forEach((currentCompositionItem: MPlaza.CompositionItem) => {
				const inTemplate = compositionItemsFromTemplate.find(
					(compositionItem) => compositionItem.get('attributeID') === currentCompositionItem.get('attributeID')
				);
				if (!inTemplate) newCompositionItems.add(currentCompositionItem);
			});

			try {
				console.log(
					'%c[ZK-LINK-FIX] applyTemplate merge: template=' +
						compositionItemsFromTemplate.length +
						' current=' +
						compositionItems.length +
						' merged=' +
						newCompositionItems.length,
					'color:#e0e;font-weight:bold'
				);
			} catch (e) {}

			this.composition.set('compositionItems', newCompositionItems);

			// await this.bootstrapComposition(this.composition);
			this.selection.setupOnComposition(this.composition);

			await this.sync3DAndPlatformSelectionWithCurrentSelection();

			this.buildStructure();
			this.updatePrice();

			this.selection.getAllAttributeSelections().forEach((attributeSelection) => {
				const selectionOption = attributeSelection.get('options').find((x) => x.get('selected'));

				// Change platform selection
				let platformSelection = this.platformAttributesSelection.find((x) => {
					return matchCodes(x.attributeCode, attributeSelection.get('attributeCode')!);
				});

				if (platformSelection && selectionOption) {
					platformSelection.optionCode = selectionOption.get('optionCode')!;
					platformSelection.optionLabel = this.T_dynamic(selectionOption.get('name')!);
				}
				console.log({ platformSelection });
			});
		} catch (ex) {
			console.error('Error applying template: ', ex);
		}
		console.log('sono result in applyTemplate', result);
		// applico il design se questo è presente nel template salvato, lo salviamo come designID
		if (result.designID && result.designID !== -1) {
			this.shouldLoadDesign = true;
			await this.internalLoadDesign(result.designID.toString(), true);
		}
	};

	public configureByAI = async (text: string) => {
		try {
			const { data: options } = await axiosApi.get<{ attributeName: string; optionName: string }[]>('ai/configuration-by-prompt', {
				params: {
					productId: this.zkProduct!.get('modelID'),
					prompt: text
				}
			});

			for (const { attributeName, optionName } of options) {
				const attribute = this.zkProduct!.get('attributes').find((attr) => attr.get('name') === attributeName);
				if (!attribute) continue;

				const optionToSelect = attribute.get('options').find((opt) => opt.get('name') === optionName);
				if (optionToSelect) {
					this.selectOption(optionToSelect.get('optionID'));
				}
			}
		} catch (error) {
			console.error('Error configuring by AI:', error);
		}
	};

	public getTryOnProvidersList = async () => {
		// Early return if we already have cached settings for the current product
		if (this.tryOnProvidersSettings) return this.tryOnProvidersSettings;

		try {
			const { data } = await axiosApi.get<TryOnProvidersTypesMapping>(`${Zakeke.config.tryOnApiUrl}tryonprovider/list`);
			this.tryOnProvidersSettings = data;
			return data;
		} catch (error) {
			console.error('Failed to fetch try-on providers:', error);
			return null;
		}
	};

	public getTryOnProductInfo = async () => {
		// Early return if we already have cached settings for the current product
		if (this.tryOnProductSettings?.productID === this.internalProduct?.get('modelID')) {
			return this.tryOnProductSettings;
		}

		const productID = this.internalProduct?.get('modelID');
		if (!productID) return null;

		try {
			const { data } = await axiosApi.get<TryOnProduct>(`${Zakeke.config.tryOnApiUrl}tryonproduct/${productID}`);
			this.tryOnProductSettings = data;
			return data;
		} catch (error) {
			console.error('Failed to fetch try-on product info:', error);
			return null;
		}
	};

	public getTryOnSellerInfo = async (typeID: number, providerID: number) => {
		if (!this.settings) return null;
		// Early return if we already have cached settings for the current product
		if (this.tryOnSellerSettings) return this.tryOnSellerSettings;

		try {
			const { data } = await axiosApi.get<TryOnSeller>(`${Zakeke.config.tryOnApiUrl}tryoncustomer/?typeID=${typeID}&providerID=${providerID}`);
			this.tryOnSellerSettings = data;
			return data;
		} catch (error) {
			console.error('Failed to fetch try-on seller info:', error);
			return null;
		}
	};

	private getMessagesByProductCode = async (productCode: string) => {
		const query = {
			name: 'messagesByProduct',
			query: `query getEventMessagesByProduct($productGlobalCode: String!){
							messagesByProduct(productGlobalCode: $productGlobalCode){
								id
								eventGlobalID
								messageId
								messageText
								eventName
								eventId
								closeable
								visible
								addToCartDisabledValue
								enableCheckForSellerImagesValue
								enableAdditionalMessage
								additionalMessage
								eventMessageId
								title
								titleSingleMessage
								infoLink
								defaultMessage
								visibleOnlyOnApi
								addToCartLabel
								visibleLowQualityInfoText
								defaultAdditionalMessageText
								warningText
								onDeleteWarningText
								isDefault
							}
						}`,
			variables: { productGlobalCode: 'guid://Zakeke/Model/' + productCode }
		};
		let response: GraphQLWelcomeMessageResponse | null = null;
		try {
			if (typeof Zakeke.config.cloudFlareApiUrl !== 'undefined' && Zakeke.config.cloudFlareApiUrl) {
				response = (await axiosApi.post<GraphQLWelcomeMessageResponse>(Zakeke.config.cloudFlareApiUrl + 'graphql', query))?.data ?? null;
			}
		} catch (error) {
			console.error('Error in CloudFlare API', error);
		}
		try {
			if (response === null) {
				response = (await axiosApi.post<GraphQLWelcomeMessageResponse>(Zakeke.config.graphqlUrl + 'graphql', query))?.data ?? null;
			}
		} catch (error) {
			console.error('Error in GraphQL API', error);
		}
		let result: EventMessage[] | null = null;
		try {
			if (response?.data?.messagesByProduct) {
				result =
					(response?.data?.messagesByProduct
						.map((message) => {
							return {
								eventID: message.eventId,
								description: message.messageText,
								closeable: message.closeable,
								visible: message.visible,
								addToCartDisabledIfVisible: message.addToCartDisabledValue,
								additionalData: message.additionalMessage,
								enableCheckForSellerImages: message.enableCheckForSellerImagesValue,
								isDefault: message.isDefault
							};
						})
						.map((message) => EventMessage.fromMessage(message)) as EventMessage[]) ?? [];
			}
		} catch (error) {
			console.error('Error in mapping welcome messages response', error);
		}
		return result;
	};

	public get hasVTryOnEnabled(): boolean {
		if (!this.zkScene || !this.tryOnSellerSettings || !this.tryOnProductSettings) return false;
		if (this.tryOnSellerSettings.vtoState === SellerTryOnState.NotActive) return false;

		try {
			const settings = JSON.parse(this.tryOnProductSettings.settings);
			const tryOnSettings = this.zkScene.getTryOnSettings();

			return settings.active && this.zkScene.getTryOnEnabled() && tryOnSettings && tryOnSettings.typeSettings.active;
		} catch (error) {
			console.error('Error parsing try-on settings:', error);
			return false;
		}
	}

	public get canUseTryOn(): boolean {
		if (!this.tryOnSellerSettings || this.tryOnSellerSettings.vtoState === SellerTryOnState.NotActive || !this.tryOnProductSettings) return false;

		try {
			const settings = JSON.parse(this.tryOnProductSettings.settings);
			if (!settings.try_on) return false;

			const tryOnSettings = this.zkScene?.getTryOnSettings();
			return !(tryOnSettings && tryOnSettings.typeSettings.try_on);
		} catch (error) {
			console.error('Error parsing try-on settings:', error);
			return false;
		}
	}

	public get canUsePD(): boolean {
		if (!this.tryOnSellerSettings || this.tryOnSellerSettings.vtoState === SellerTryOnState.NotActive || !this.tryOnProductSettings) return false;

		try {
			const settings = JSON.parse(this.tryOnProductSettings.settings);
			if (!settings.pd) return false;

			const tryOnSettings = this.zkScene?.getTryOnSettings();
			return !(tryOnSettings && tryOnSettings.typeSettings.pd);
		} catch (error) {
			console.error('Error parsing try-on settings:', error);
			return false;
		}
	}

	public get isVisibleMeshShownForTryOn(): boolean {
		return this.viewer?.isTryOnMeshVisibleEnabledToShow();
	}

	public get isMandatoryPD(): boolean {
		return this.isPupillaryDistanceMandatory;
	}

	public get isTryOnMeshVisible(): boolean {
		return this.viewer?.getTryOnMeshVisible() != null;
	}

	public get tryOnMeshVisibleSettings(): TryOnGenericFineTuningSettings | undefined {
		if (this.isTryOnMeshVisible) {
			try {
				const finetuningData = this?.viewer?.getDAMFineTuningData() || undefined;
				if (finetuningData) return finetuningData;
			} catch (ex) {
				console.error('Error parsing DAM fine tuning data', ex);
			}
			const tryOnMesh = this?.viewer?.getTryOnFineTuningMeshVisible();
			if (tryOnMesh) {
				return this.viewer.getMeshFineTuningData(tryOnMesh) || undefined;
			}
		}
		return undefined;
	}

	public setPDDistance = (distance: number) => {
		this.pdSettings = distance;
	};

	public pdDistance = () => {
		return this.pdSettings;
	};

	public exportTryOnMeshToGlb = async () => {
		if (!this.zkScene) return null;
		if (!this.viewer) return null;
		const tryOnMesh = this.viewer.getTryOnMeshVisible();
		if (!tryOnMesh) return null;
		return this.viewer.exportMeshToGLBBlob(tryOnMesh.id);
	};

	public getTryOnSettings = () => {
		if (this.zkScene) return this.zkScene.getTryOnSettings();
		return undefined;
	};

	public getDeepARDesktopIframeUrl = async (): Promise<string> => {
		let finalUrl = '';

		try {
			const blob = await this.exportTryOnMeshToGlb();
			const formData = new FormData();
			const glbModel = await blob!.arrayBuffer();
			const randomString = Math.random().toString(36).substring(7);
			formData.append('blob', new Blob([glbModel]), `model_${randomString}.glb`);

			const response = await fetch(Zakeke.config.baseApiUrl + 'tryonassetupload/asset-upload', {
				method: 'POST',
				body: formData,
				headers: {
					Accept: 'application/json',
					Authorization: 'Bearer ' + window['token']
				}
			});

			if (response.ok) {
				const data = await response.json();
				const url = data;

				// Costruisco i parametri di query una volta caricato il modello
				let settings = this.tryOnMeshVisibleSettings || DefaultDeepARFineTuningSettings;

				const queryParams = new URLSearchParams({
					positionX: settings!.position.x.toString(),
					positionY: settings!.position.y.toString(),
					positionZ: settings!.position.z.toString(),
					scale: settings.scale.toString(),
					environment: settings!.environment!.toString()
				});

				finalUrl = `${url}&${queryParams.toString()}&isDesktopDeepARUrl=true`;
			} else {
				console.error('Error uploading model', response);
			}
		} catch (e) {
			console.error('Error uploading model', e);
		}

		return finalUrl;
	};

	public tryOnCountView = async (sellerID: number, providerID: number, modeChosen: TryOnMode) => {
		try {
			await axiosApi.post(Zakeke.config.tryOnApiUrl + 'viewscount/tryon/view', {
				sellerID: sellerID,
				providerID: providerID,
				mode: modeChosen
			});
		} catch (error) {
			console.error('Error in tryOnCountView', error);
		}
	};

	/**
	 * Ottiene le restrizioni sul printing method (che nel caso di un prodotto configuratore 3D c'è sempre ed è 1 solo).
	 * @remarks
	 * Se non ci sono restrizioni, ritorna quelle di default
	 * @returns
	 * Le restrizioni sul printing method o quelle di default
	 */
	public getPrintingMethodsRestrictions = () => {
		let defaultColor: RestrictionColor = {
			colorCode: '#000000',
			isDefault: true
		};
		let restrictions: PrintingMethodsRestrictions = {
			allowedBold: true,
			allowedItalic: true,
			allowedCurved: true,
			disableTextColors: false,
			textColors: [defaultColor],
			defaultColor: '#000000',
			isPDFPreviewEnabled: true
		};
		if (this.zkProduct && this.zkProduct.get('printTypes').length > 0) {
			const printType = this.zkProduct.get('printTypes').at(0);
			const printRestrictions = printType.get('printTypeRestrictions');
			if (printRestrictions) {
				defaultColor = {
					...defaultColor,
					colorCode: printRestrictions.get('defaultColor') ?? '#000000'
				};
				let textColors = printRestrictions.get('textColors') ?? [];
				if (!textColors.find((color) => color.colorCode === defaultColor.colorCode)) textColors.push(defaultColor);

				restrictions = {
					allowedBold: printRestrictions.get('allowBold') ?? true,
					allowedItalic: printRestrictions.get('allowItalic') ?? true,
					allowedCurved: printRestrictions.get('curvedText') ?? true,
					disableTextColors: printRestrictions.get('disableTextColors') ?? false,
					textColors: textColors,
					defaultColor: printRestrictions.get('defaultColor') ?? '#000000',
					isPDFPreviewEnabled: printRestrictions.get('canPreviewDesignsPDF') ?? true
				};
			}
		}
		return restrictions;
	};
	public setMouseWheelZoomEnabled = (enabled: boolean) => {
		this.viewer.setMouseWheelInputEnabled(enabled);
	};

	public setCameraPinchingZoom = (toggle: boolean) => {
		this.viewer.setCameraPinchingZoom(toggle);
	};

	public get isAiEditorEnabled() {
		return this.isAIStudioEditorEnabled;
	}

	public startAIStudio = async (): Promise<void> => {
		if (!this.isAIStudioShoppingPreviewEnabled && !this.isAIStudioEditorEnabled) {
			console.warn('AI Studio is not enabled');
			return;
		}
		const attributeSelection = this.selection.getAllAttributeSelections().map((attributeSelection) => {
			const selectedOption = attributeSelection.getSelectedOption();
			return {
				attributeId: attributeSelection.get('attributeID'),
				optionId: selectedOption ? selectedOption.get('optionID') : null
			};
		});

		await this.saveComposition();
		const compositionDocId = this.composition.get('docID');

		const productID = this.zkProduct?.get('modelID');
		window.parent.postMessage(
			{
				type: 'OPEN_AI_STUDIO',
				params: {
					attributeSelection: attributeSelection,
					productId: productID,
					compositionDocId: compositionDocId
				}
			},
			'*'
		);
	};

	// AI Studio methods - cached API responses to avoid duplicate calls
	private _aiPresetCategoriesPromise: Promise<AIPresetCategory[]> | null = null;
	private _aiPresetPresetsPromise: Promise<AIPreset[]> | null = null;

	private fetchAllAIPresetCategories = (): Promise<AIPresetCategory[]> => {
		if (!this._aiPresetCategoriesPromise) {
			this._aiPresetCategoriesPromise = axiosApi
				.get<AIPresetCategory[]>('ai/aipreset/categories')
				.then((res) => res.data)
				.catch((error) => {
					console.log('Error fetching AI Preset Categories:', error);
					this._aiPresetCategoriesPromise = null;
					return [] as AIPresetCategory[];
				});
		}
		return this._aiPresetCategoriesPromise;
	};

	private fetchAllAIPresetPresets = (): Promise<AIPreset[]> => {
		if (!this._aiPresetPresetsPromise) {
			this._aiPresetPresetsPromise = axiosApi
				.get<AIPreset[]>('ai/aipreset/presets')
				.then((res) => res.data)
				.catch((error) => {
					console.log('Error fetching AI Preset Presets:', error);
					this._aiPresetPresetsPromise = null;
					return [] as AIPreset[];
				});
		}
		return this._aiPresetPresetsPromise;
	};

	public getAIPresetCategories = async (presetType: string): Promise<AIPresetCategory[]> => {
		try {
			const allCategories = await this.fetchAllAIPresetCategories();

			// Filter by isActive and presetType
			const categories = allCategories.filter(
				(category: AIPresetCategory) =>
					category.isActive === true && (category.presetType === presetType || (category.toolId === 'all' && category.presetType === ''))
			);

			// Check if there are valid categories
			const hasValidCategories = categories.some((category: AIPresetCategory) => !(category.toolId === 'all' && category.presetType === ''));

			return hasValidCategories ? categories : [];
		} catch (error) {
			console.log('Error fetching AI Preset Categories:', error);
			return [];
		}
	};

	public getAIPresetPresets = async (presetType: string): Promise<AIPreset[]> => {
		try {
			const allPresets = await this.fetchAllAIPresetPresets();

			// Filter by presetType and isActive
			return allPresets.filter((preset: AIPreset) => preset.isActive === true && preset.presetType === presetType);
		} catch (error) {
			console.log('Error fetching AI Preset Presets:', error);
			return [];
		}
	};

	public getApplicableAIStagingRules = async (options: { productID?: number; printTypeID?: number }): Promise<AIProductStagingRule | null> => {
		const { productID, printTypeID } = options;

		if (!productID && !printTypeID) {
			throw new Error('At least one of productID or printTypeID must be provided');
		}

		try {
			const queryParams = new URLSearchParams();
			if (productID) queryParams.append('productID', productID.toString());
			if (printTypeID) queryParams.append('printTypeID', printTypeID.toString());

			return (await axiosApi.get<AIProductStagingRule>(`v1/AIProductStagingRule/applicable?${queryParams.toString()}`)).data;
		} catch (error) {
			console.log('Error fetching applicable AI staging rules:', error);
			return null;
		}
	};

	public getApplicableAIVirtualModelRules = async (options: { productID?: number; printTypeID?: number }): Promise<AIVirtualModelRule | null> => {
		const { productID, printTypeID } = options;

		if (!productID && !printTypeID) {
			throw new Error('At least one of productID or printTypeID must be provided');
		}

		try {
			const queryParams = new URLSearchParams();
			if (productID) queryParams.append('productID', productID.toString());
			if (printTypeID) queryParams.append('printTypeID', printTypeID.toString());

			const url = `v1/AIVirtualModelRule/applicable?${queryParams.toString()}`;
			console.log('[AI-Studio] Fetching VTO rules from:', url);
			const result = (await axiosApi.get<AIVirtualModelRule>(url)).data;
			console.log('[AI-Studio] VTO rules response:', result);
			return result;
		} catch (error) {
			console.log('[AI-Studio] Error fetching applicable AI virtual model rules:', error);
			return null;
		}
	};

	private fetchServicesAvailability = async (): Promise<AIServicesAvailability> => {
		try {
			const url = Zakeke.config.baseApiUrl + 'subscription/ServiceCredits/allservicesavailability';
			console.log('[AI-Studio] Fetching services availability from:', url);
			// Use baseApiUrl directly to bypass CloudFlare (matches customizer-react's skipCloudflare behavior)
			const response = await axiosApi.get(url);
			const data = response.data as Record<string, boolean>;
			console.log('[AI-Studio] Services availability raw response:', data);

			// The axiosApi interceptor runs pascalToCamel on all keys, which lowercases the first char.
			// So GENERATIVE_AI_VTO → gENERATIVE_AI_VTO, VECTORIZE → vECTORIZE.
			// We normalize here to keep the interface clean.
			const result: AIServicesAvailability = {
				GENERATIVE_AI_DESIGN: data.gENERATIVE_AI_DESIGN ?? data.GENERATIVE_AI_DESIGN ?? false,
				GENERATIVE_AI_IMAGE_GENERATOR: data.gENERATIVE_AI_IMAGE_GENERATOR ?? data.GENERATIVE_AI_IMAGE_GENERATOR ?? false,
				GENERATIVE_AI_PRODUCT_STAGING: data.gENERATIVE_AI_PRODUCT_STAGING ?? data.GENERATIVE_AI_PRODUCT_STAGING ?? false,
				GENERATIVE_AI_VTO: data.gENERATIVE_AI_VTO ?? data.GENERATIVE_AI_VTO ?? false,
				VECTORIZE: data.vECTORIZE ?? data.VECTORIZE ?? false
			};
			console.log('[AI-Studio] Services availability normalized:', result);
			return result;
		} catch (error) {
			console.log('[AI-Studio] Error fetching AI services availability:', error);
			return {
				GENERATIVE_AI_DESIGN: false,
				GENERATIVE_AI_IMAGE_GENERATOR: false,
				GENERATIVE_AI_PRODUCT_STAGING: false,
				GENERATIVE_AI_VTO: false,
				VECTORIZE: false
			};
		}
	};

	private createAIProductStagingTask = async (options: AIProductStagingOptions): Promise<string | null> => {
		const payload = {
			ProductImageUrl: options.productImageUrl,
			BackgroundType: options.backgroundType ?? 'neutral',
			BackgroundPreset: options.backgroundPreset,
			BackgroundImageUrl: options.backgroundImageUrl,
			SurfacePreset: options.surfacePreset,
			MoodPreset: options.moodPreset,
			CustomPrompt: options.customPrompt,
			EffectPreset: options.effectPreset
		};

		console.log('[AI Product Staging] Creating task with payload:', {
			...payload,
			ProductImageUrl: payload.ProductImageUrl ? `${payload.ProductImageUrl.substring(0, 50)}... (${payload.ProductImageUrl.length} chars)` : null
		});

		try {
			// Use baseApiUrl instead of cloudFlareApiUrl (same as customizer)
			const url = Zakeke.config.baseApiUrl + 'v1/ai/productstaging';
			console.log('[AI Product Staging] POST URL:', url);

			const response = await fetch(url, {
				method: 'POST',
				headers: {
					Authorization: 'Bearer ' + this.token,
					'Accept-Language': this.culture,
					'Content-Type': 'application/json',
					'ZKK-Session-ID': this.aiSessionId
				},
				body: JSON.stringify(payload)
			});

			if (!response.ok) {
				const json = await response.json().catch(() => null);

				// Rate limit error (429 with maxOperations in body)
				if (response.status === 429 && json && 'maxOperations' in json) {
					const rule: AIToolUsageRule = {
						toolType: 'CustomizerProductStaging',
						hasRule: true,
						toolUsageRuleID: null,
						ruleLevel: null,
						allowLoggedUsers: null,
						allowNonLoggedUsers: null,
						limitEnabled: true,
						limitPeriodType: (json.type as AILimitPeriodType) || 'Minutes',
						maxOperations: json.maxOperations,
						limitPeriodMinutes: json.limitPeriod || null
					};
					throw new AIRateLimitError(json.reason || 'Rate limit exceeded', rule, this.lastProductStagingGenerationTime);
				}

				console.error('[AI Product Staging] HTTP error:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			console.log('[AI Product Staging] Task created successfully:', data);
			return data?.taskId ?? null;
		} catch (error) {
			if (error instanceof AIRateLimitError) {
				throw error;
			}
			console.error('[AI Product Staging] Error creating task:', error);
			return null;
		}
	};

	private pollAIProductStagingTask = async (
		taskId: string,
		progress?: (progress: number) => void
	): Promise<{ result: AIProductStagingResult | null; message: string }> => {
		let times = 0;
		const emptyResponse = { result: null, message: '' };

		console.log(`[AI Product Staging] Starting polling for task ${taskId}`);

		for (;;) {
			try {
				// Use baseApiUrl instead of cloudFlareApiUrl (same as customizer)
				const url = Zakeke.config.baseApiUrl + `ai/productstaging/tasks/${taskId}`;

				const fetchResponse = await fetch(url, {
					method: 'GET',
					headers: {
						Authorization: 'Bearer ' + this.token,
						'Accept-Language': this.culture,
						'Content-Type': 'application/json',
						'ZKK-Session-ID': this.aiSessionId
					}
				});

				if (!fetchResponse.ok) {
					console.error('[AI Product Staging] HTTP error during polling:', fetchResponse.status, fetchResponse.statusText);
					return { ...emptyResponse, message: `HTTP error: ${fetchResponse.status}` };
				}

				const response: AITaskResponse = await fetchResponse.json();

				// Log every 5 attempts or on state change
				if (times % 5 === 0 || response.state !== 0) {
					console.log(`[AI Product Staging] Polling attempt ${times + 1}/200 - State: ${response.state}`, response);
				}

				// Update progress if callback is provided
				if (progress) {
					progress(Math.min(times + 1, 100));
				}

				// State 2 = Completed successfully
				if (response && response.state === 2 && response.resultUrl) {
					console.log('[AI Product Staging] Task completed successfully:', response.resultUrl);
					return {
						result: {
							imageUrl: response.resultUrl,
							taskId: taskId
						},
						message: response.errorMessage ?? ''
					};
				}
				// State 3 = Failed
				else if (response && response.state === 3) {
					console.error('[AI Product Staging] Task failed:', response.errorMessage);
					return { ...emptyResponse, message: response.errorMessage ?? 'Task failed' };
				}

				times++;
				if (times > 200) {
					console.error('[AI Product Staging] Task timeout after 200 attempts');
					return { ...emptyResponse, message: 'Task timeout' };
				}

				// Delay 1s
				await new Promise((resolve) => setTimeout(resolve, 1000));
			} catch (error) {
				console.error('[AI Product Staging] Error during polling:', error);
				return { ...emptyResponse, message: `Polling error: ${error}` };
			}
		}
	};

	public generateAIProductStaging = async (options: AIProductStagingOptions): Promise<AIProductStagingResult> => {
		console.log('[AI Product Staging] Starting generation with options:', {
			...options,
			productImageUrl: options.productImageUrl ? `${options.productImageUrl.substring(0, 50)}... (${options.productImageUrl.length} chars)` : null
		});

		if (!options.productImageUrl) {
			throw new Error('No product image URL provided for AI product staging.');
		}

		const taskId = await this.createAIProductStagingTask(options);

		if (!taskId) {
			throw new Error('Error creating AI product staging task');
		}

		const { result, message } = await this.pollAIProductStagingTask(taskId, options.progress);

		if (!result) {
			throw new Error(message || 'Failed to generate AI product staging');
		}

		// Track last generation time on success
		this.lastProductStagingGenerationTime = Date.now();

		console.log('[AI Product Staging] Generation completed successfully');
		return result;
	};

	private createAIVirtualTryonTask = async (options: AIVirtualTryOnOptions): Promise<string | null> => {
		const payload = {
			PersonImageUrl: options.personImageUrl,
			PersonPreset: options.personPreset,
			ClothingImageUrl: options.clothingImageUrl,
			ClothingPreset: options.clothingPreset,
			BackgroundImageUrl: options.backgroundImageUrl,
			BackgroundPreset: options.backgroundPreset,
			CustomPrompt: options.customPrompt,
			EffectPreset: options.effectPreset,
			PosePreset: options.posePreset
		};

		try {
			// Use baseApiUrl instead of cloudFlareApiUrl (same as customizer)
			const url = Zakeke.config.baseApiUrl + 'v1/ai/virtualtryon';

			const response = await fetch(url, {
				method: 'POST',
				headers: {
					Authorization: 'Bearer ' + this.token,
					'Accept-Language': this.culture,
					'Content-Type': 'application/json',
					'ZKK-Session-ID': this.aiSessionId
				},
				body: JSON.stringify(payload)
			});

			if (!response.ok) {
				const json = await response.json().catch(() => null);

				// Rate limit error (429 with maxOperations in body)
				if (response.status === 429 && json && 'maxOperations' in json) {
					const rule: AIToolUsageRule = {
						toolType: 'CustomizerVirtualTryOn',
						hasRule: true,
						toolUsageRuleID: null,
						ruleLevel: null,
						allowLoggedUsers: null,
						allowNonLoggedUsers: null,
						limitEnabled: true,
						limitPeriodType: (json.type as AILimitPeriodType) || 'Minutes',
						maxOperations: json.maxOperations,
						limitPeriodMinutes: json.limitPeriod || null
					};
					throw new AIRateLimitError(json.reason || 'Rate limit exceeded', rule, this.lastVirtualTryonGenerationTime);
				}

				console.error('[AI Virtual Try-on] HTTP error:', response.status, response.statusText);
				return null;
			}

			const data = await response.json();
			return data?.taskId ?? null;
		} catch (error) {
			if (error instanceof AIRateLimitError) {
				throw error;
			}
			console.log('Error creating AI virtual try-on task:', error);
			return null;
		}
	};

	private pollAIVirtualTryonTask = async (
		taskId: string,
		progress?: (progress: number) => void
	): Promise<{ result: AIVirtualTryOnResult | null; message: string }> => {
		let times = 0;
		const emptyResponse = { result: null, message: '' };

		while (times < 60) {
			try {
				// Use baseApiUrl instead of cloudFlareApiUrl (same as customizer)
				const url = Zakeke.config.baseApiUrl + `ai/virtualtryon/tasks/${taskId}`;

				const fetchResponse = await fetch(url, {
					method: 'GET',
					headers: {
						Authorization: 'Bearer ' + this.token,
						'Accept-Language': this.culture,
						'Content-Type': 'application/json',
						'ZKK-Session-ID': this.aiSessionId
					}
				});

				if (!fetchResponse.ok) {
					console.error('[AI Virtual Try-on] HTTP error during polling:', fetchResponse.status, fetchResponse.statusText);
					return { ...emptyResponse, message: `HTTP error: ${fetchResponse.status}` };
				}

				const response: AITaskResponse = await fetchResponse.json();

				if (response.state === 2 && response.resultUrl) {
					if (progress) progress(100);
					return {
						result: {
							taskId,
							imageUrl: response.resultUrl
						},
						message: 'Success'
					};
				} else if (response.state === 3) {
					return { ...emptyResponse, message: response.errorMessage ?? 'Task failed w/o error message' };
				}

				// If it's still running, wait 2 seconds and try again
				if (progress) progress(Math.round((times / 60) * 90));
				await new Promise((resolve) => setTimeout(resolve, 2000));
				times++;
			} catch (error) {
				return { ...emptyResponse, message: 'HTTP error' };
			}
		}

		return { ...emptyResponse, message: 'Timeout' };
	};

	public generateAIVirtualTryon = async (options: AIVirtualTryOnOptions): Promise<AIVirtualTryOnResult> => {
		const taskId = await this.createAIVirtualTryonTask(options);

		if (!taskId) {
			throw new Error('Error creating AI virtual try-on task');
		}

		const { result, message } = await this.pollAIVirtualTryonTask(taskId, options.progress);

		if (!result) {
			throw new Error(message || 'Failed to generate AI virtual try-on');
		}

		// Track last generation time on success
		this.lastVirtualTryonGenerationTime = Date.now();

		return result;
	};
}
